/** Generate Video node — "生成视频" unified Video Generation Node.
 *
 * Canvas media UX unification V1: MEDIA BODY + EXPANDED EDITOR.
 * Keyframe START/MIDDLE/END UI is intentionally hidden this round
 * (backend keyframe contract still honored for pre-existing edges). */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  DownOutlined,
  LoadingOutlined,
  PushpinFilled,
  PushpinOutlined,
  RightOutlined,
  SoundOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Modal, Tooltip, Input, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  getAuthMatrixRevision,
  getAuthMatrixStatus,
  isChannelEffectiveAllowed,
  isNodeAuthDenied,
  subscribeAuthMatrix,
} from '../authMatrixRuntime'
import { useParams } from 'react-router-dom'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import CanvasActionPopover from '../CanvasActionPopover'
import {
  createVideoGenTask,
  getVideoGenTask,
  materializeCanvasVideoAsset,
  videoGenTaskOutputUrl,
  videoGenTaskPosterUrl,
  type SaasApiError,
  type VideoGenConfig,
  type VideoGenEnginesResponse,
  type VideoGenKeyframeItem,
  type VideoGenTask,
  type VideoGenTaskStatus,
  type VideoKeyframeRole,
} from '../../../../api'
import { useNodeDefinition } from '../nodeRegistry'
import MediaLightbox from '../MediaLightbox'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { downloadCanvasMedia } from '../canvasDownload'
import {
  computeVideoConfigFingerprint,
  computeVideoGenerationDriftState,
  computeVideoUpstreamFingerprint,
  generationDriftBannerText,
  generationDriftInspectorText,
} from '../generationInputFingerprint'
import { resolvePromptForSubmit, countDisconnectedMentions, stripMentionsForEdge } from '../assetMention'
import {
  GENERATE_VIDEO_NODE_AGGREGATE_HANDLE,
  GENERATE_VIDEO_REFERENCE_ASSETS_AGGREGATE_HANDLE,
  GENERIC_INPUT_HANDLE,
  VIDEO_AUDIO_REFERENCE_HANDLE,
  VIDEO_AUDIO_ROLE_DEFAULT,
  VIDEO_AUDIO_ROLE_VALUES,
  VIDEO_KEYFRAME_END_HANDLE,
  VIDEO_KEYFRAME_MIDDLE_HANDLE,
  VIDEO_KEYFRAME_START_HANDLE,
  VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
  VIDEO_PROMPT_ENHANCEMENT_HANDLE,
  VIDEO_REFERENCE_VIDEO_HANDLE,
  computeMaterialAliases,
  isVideoAudioReferenceRole,
  videoStructuredAssetHandleId,
  structuredAssetSlotsFor,
  buildStructuredAssetContextSnapshot,
  type KeyframeAssignment,
  type KeyframeSlotMaterial,
  type VideoAudioReferenceRole,
  type VideoStructuredAssetCategory,
} from '../dataflow'
import { useGenerateVideoSlotDropHighlight } from '../useGenerateVideoSlotDropHighlight'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import { usePinnedExpandedNode } from '../useCanvasNodePin'
import {
  shouldForceRunHydratingOnMount,
  shouldShowActiveGenerationOutput,
} from '../canvasGenerationActivePreview'
import CanvasVideoPreview, { type CanvasVideoPreviewHandle } from '../CanvasVideoPreview'
import { functionCardShowsInspector, isStickyMediaPlayback } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { generationSlotIngressShellClass } from '../generationSlotIngress'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { computeMediaBoxSize } from '../mediaBoxSize'
import { resolveGenerationCardWidth } from '../canvasCollapsedResultFrameContract'
import type { FlowNodeData } from '../types'
import { getPlaybackTime } from '../videoPlaybackCoordinator'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import {
  generationFooterRunLabel,
  generationResultPlaceholderLabel,
} from '../canvasGenerationStatusLabels'
import { useCanvasGenerationSubmission } from '../useCanvasGenerationSubmission'
import { generationNodeCardWidth } from '../generationNodeWidthPresets'
import {
  VIDEO_SKILL_KEY,
  VIDEO_SKILL_VERSION,
  buildVideoGenerationSubmitPayload,
  channelDurationChoices,
  channelQualityChoices,
  channelRatioChoices,
  channelSupportsAudioInput,
  authFilteredVideoProviderChannels,
  parseProviderChannels,
  resolveCanvasVideoTaskFieldStatus,
  resolveCapabilityEngineKey,
  resolveDefaultProviderChannelId,
  isVideoProviderChannelExecutionSupported,
  mergeCanonicalReferenceImageAssetIds,
  resolveProviderChannel,
  usesExplicitVideoProviderChannel,
} from '../videoGenerationProvider'
import {
  clearEnhancedPromptPatch,
  markEnhancedPromptManualOverride,
  resolveEffectiveVideoPrompt,
} from '../videoPromptEnhancement'
import {
  isVideoEngineStartingError,
  localizeVideoGenerationSubmitError,
} from '../videoGenerationErrors'
import {
  formatParameterAdjustmentHint,
  normalizeVideoGenerationParameters,
  resolveVideoGenerationOperation,
} from '../videoGenerationParameterCapability'
import { loadVideoGenConfigDeduped, loadVideoGenEnginesDeduped } from '../canvasSharedConfigLoaders'
import InputMaterialsRow from './InputMaterialsRow'
import AssetMentionPromptEditor from './AssetMentionPromptEditor'
import { useMaterialThumbMap } from './useMaterialThumbMap'
import KeyframeTimelineStrip from './KeyframeTimelineStrip'
import KeyframeDetailPanel from './KeyframeDetailPanel'
import VideoInputAssetsSection, {
  genInputAssetRefId,
  structuredAssetMentionKey,
  type VideoInputAssetDisplayRef,
} from './VideoInputAssetsSection'
import { resolveCanvasImageHoverPreviewUrl, resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import {
  applyProductionFormatChange,
  buildStandaloneVideoFormatFields,
  normalizeProductionAspectRatio,
  normalizeProductionResolution,
  recommendedProductionFormat,
  type ProductionAspectRatio,
  type ProductionResolution,
} from '../storyboardProductionFormat'
import { prioritizeRefsForKeyframe, referenceAssetsFingerprint } from '../videoShotReferenceAssets'
import {
  buildVisualConditioningDiagnostics,
  hasVisualConditioningInput,
  resolveVideoVisualConditioning,
} from '../videoVisualConditioning'
import {
  addDialogueSegment,
  addKeyframeEntry,
  computeCapabilityWarnings,
  computeRequiredCapabilities,
  computeStructuredReferenceCapabilityWarning,
  inputAssetRefsForKeyframe,
  keyframeMaterialForEntry,
  keyframeSourceType,
  legacyTimelineFromAssignment,
  removeKeyframeEntry,
  updateKeyframeDurationToNext,
  updateKeyframeEntry,
  withDerivedRoles,
  type DialogueSegment,
  type InputAssetCategory,
  type InputAssetRef,
  type KeyframeTimelineEntry,
} from '../videoKeyframeTimeline'

const HEADER_HEIGHT = 34
const POLL_INTERVAL_MS = 2500
// VistoeraTASK-00143 §五/§七 — stable empty defaults (never a fresh
// `[]`/`{}` literal per render) so memoized callers keyed on these
// values don't invalidate every render for nodes with no audio wired.
const EMPTY_AUDIO_REFERENCE_SLOTS: KeyframeSlotMaterial[] = []
const EMPTY_AUDIO_ROLE_MAP: Record<string, unknown> = {}
/** Collapsed Compact Input Rail (§五/§六) — vertical offset between the
 * image/generic handle and the Prompt Enhancement handle so the two
 * stay visually distinguishable even collapsed (never the exact same
 * `top` coordinate as `GENERIC_INPUT_HANDLE`). */

/** Canonical WIDE preset for expanded shell; collapsed hugs media result carrier. */
const VIDEO_CARD_WIDTH = generationNodeCardWidth('generate_video')
const MIN_PREVIEW_HEIGHT_EXPANDED = 240
const MAX_PREVIEW_HEIGHT_EXPANDED = 360

function computePreviewBox(ratio: string): { width: number; height: number } {
  const [wRaw, hRaw] = ratio.split(':')
  const w = Number(wRaw) || 16
  const h = Number(hRaw) || 9
  return computeMediaBoxSize(Math.max(1, w) * 100, Math.max(1, h) * 100)
}

function computeExpandedPreviewHeight(ratio: string, width: number): number {
  const [wRaw, hRaw] = ratio.split(':')
  const w = Number(wRaw) || 16
  const h = Number(hRaw) || 9
  const raw = Math.round((width * h) / w)
  return Math.min(MAX_PREVIEW_HEIGHT_EXPANDED, Math.max(MIN_PREVIEW_HEIGHT_EXPANDED, raw))
}

const FALLBACK_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4']
const FALLBACK_DURATIONS = [5]

function isTerminalStatus(status: VideoGenTaskStatus): boolean {
  return status === 'succeeded' || status === 'failed'
}

function materializeImageSource(
  workspaceId: string,
  source: { entityId?: string; generatedOutputId?: string },
  missingDataSourceMessage: string,
): Promise<string> {
  if (source.entityId) {
    return materializeCanvasVideoAsset(workspaceId, 'site_asset', source.entityId).then((r) => r.data.asset_id)
  }
  if (source.generatedOutputId) {
    return materializeCanvasVideoAsset(workspaceId, 'image_generation_output', source.generatedOutputId).then((r) => r.data.asset_id)
  }
  return Promise.reject(new Error(missingDataSourceMessage))
}

function materializeSiteAssetRef(workspaceId: string, siteAssetId: string): Promise<string> {
  return materializeCanvasVideoAsset(workspaceId, 'site_asset', siteAssetId).then((r) => r.data.asset_id)
}

function isVideoTaskNotFound(err: unknown): boolean {
  const apiErr = err as SaasApiError
  return apiErr?.status === 404 || apiErr?.code === 'not_found'
}

function GenerateVideoNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const { t, videoQualityOptions, mediaHeader, regenerate } = useCanvasGenerationI18n()
  const displayLabel = nodeLabel(def.nodeType, def.label)
  const workspaceId = nodeData.workspaceId || ''
  const { projectId = '' } = useParams()
  const fields = nodeData.fields || {}
  const inputMaterials = nodeData.inputMaterials || []
  const keyframeAssignment: KeyframeAssignment =
    nodeData.keyframeAssignment || { middle: [], middleByEntryId: new Map<string, KeyframeSlotMaterial>() }

  const promptText = (fields.promptText as string | undefined) ?? ''
  const enhancedPromptText = (fields.enhancedPromptText as string | undefined) ?? ''
  const boundPromptEnhancerNodeId =
    typeof fields.boundPromptEnhancerNodeId === 'string' && fields.boundPromptEnhancerNodeId.trim()
      ? fields.boundPromptEnhancerNodeId.trim()
      : null
  const showEnhancedPromptSection = Boolean(enhancedPromptText.trim() || boundPromptEnhancerNodeId)
  const effectivePromptMeta = useMemo(() => resolveEffectiveVideoPrompt(fields), [fields])
  const effectivePromptSource = effectivePromptMeta.effectivePromptSource
  const ratio = (fields.ratio as string | undefined) ?? '16:9'
  // Backend-valid default (video_generation_params.DEFAULT_QUALITY_TIER) —
  // NOT the old 'STANDARD' placeholder, which always failed server-side
  // validation ("不支持的清晰度 'STANDARD'") for any node created before the
  // user ever opened "更多参数" (P0 real-E2E bug found this round).
  const qualityTier = (fields.qualityTier as string | undefined) ?? '1080P'
  const durationSeconds = (fields.durationSeconds as number | undefined) ?? 5
  const engine = (fields.engine as string | undefined) ?? 'auto'
  const providerChannel = (fields.providerChannel as string | undefined) ?? undefined
  const middleTimePosition = (fields.middleKeyframeTimePosition as number | undefined) ?? 3
  const explicitProvider = usesExplicitVideoProviderChannel(fields)

  const videoGenConfig = (nodeData.videoGenerationConfig as VideoGenConfig | null | undefined) ?? null
  useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)
  const authMatrixStatus = getAuthMatrixStatus()
  const nodeAuthDenied = isNodeAuthDenied('generate_video')
  const providerChannels = useMemo(() => parseProviderChannels(videoGenConfig), [videoGenConfig])
  // VistoeraTASK-00691 — same auth-status memo dependency as GenerateImageNode.
  const selectableProviderChannels = useMemo(
    () => authFilteredVideoProviderChannels(providerChannels, 'generate_video'),
    [providerChannels, authMatrixStatus],
  )
  const selectedProvider = useMemo(
    () => resolveProviderChannel(fields, providerChannels),
    [fields, providerChannels],
  )
  const providerAuthDenied = Boolean(
    selectedProvider?.provider_option_id &&
      !isChannelEffectiveAllowed('generate_video', selectedProvider.provider_option_id),
  )
  const noAllowedVideoProviders =
    authMatrixStatus === 'ready' &&
    selectableProviderChannels.length === 0 &&
    providerChannels.length > 0
  // VISTOERA_FUNCTION_NODE_PROVIDER_SINGLE_SOURCE_OF_TRUTH_AND_CANVAS_
  // GENERATE_VIDEO_PROVIDER_DRIFT_FORENSIC_P0_V1 §P8 — an explicitly
  // persisted `providerChannel` that no longer resolves against the
  // CURRENT (Function-Node-Provider-Binding-gated) channel list means
  // the Provider this historical Canvas node used has since been
  // disabled/removed by Admin. Never silently fall back to a different
  // Provider and never fake availability — surface it plainly and block
  // Generate until the tenant re-selects a currently allowed Provider.
  const persistedProviderUnavailable = Boolean(explicitProvider && providerChannel && !selectedProvider)
  const providerExecutionBlocked = Boolean(
    selectedProvider && !isVideoProviderChannelExecutionSupported(selectedProvider),
  )
  const providerExecutionBlockMessage =
    (selectedProvider?.billing_tooltip as string | undefined)?.trim() ||
    t('videoErrors.PROVIDER_EXECUTION_NOT_SUPPORTED')
  // VistoeraTASK-00143 §八/§九 — Provider-aware audio-input capability,
  // never a hardcoded provider-id/name branch (spec §八 hard rule: no
  // `if provider === <specific-provider-id>` anywhere in this file).
  // Computed here so BOTH the submit-time hard block AND the inline
  // "音频" section warning banner read the identical value.
  const audioInputSupportedBySelectedProvider = channelSupportsAudioInput(selectedProvider)
  // Task spec §P5/§P6 Case 4 — in-channel model/mode sub-selection. Only
  // relevant once a dynamic (multi-model) Provider channel exists; the
  // three legacy hardcoded channels each carry exactly one model, so
  // `model_choices` stays absent/length<=1 for them and this never
  // renders an extra control on the pre-existing paths.
  const modelChoices = selectedProvider?.model_choices ?? []
  const selectedModelId = useMemo(() => {
    const stored = fields.modelId as string | undefined
    if (stored && modelChoices.some((m) => m.id === stored)) return stored
    return modelChoices.find((m) => m.is_default)?.id ?? modelChoices[0]?.id
  }, [fields.modelId, modelChoices])

  // ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4/§37/§43
  // — the dynamic Keyframe Timeline / Dialogue Segment / Input Asset
  // list. `fields.keyframeTimeline` ABSENT (never touched by the new
  // UI) falls back to the legacy single-START/MIDDLE/END edge shim
  // WITHOUT ever writing it back (spec §42/§43 "手工创建默认最简单状态" +
  // "opening an old video node twice is idempotent"); PRESENT-BUT-EMPTY
  // is an explicit, valid 0-keyframe pure text-to-video state (spec §16)
  // and is never auto-migrated.
  const rawKeyframeTimeline = fields.keyframeTimeline as KeyframeTimelineEntry[] | undefined
  const keyframeTimeline = useMemo(() => {
    if (rawKeyframeTimeline) return rawKeyframeTimeline.length > 0 ? withDerivedRoles(rawKeyframeTimeline) : []
    return legacyTimelineFromAssignment(keyframeAssignment, middleTimePosition)
  }, [rawKeyframeTimeline, keyframeAssignment, middleTimePosition])
  const dialogueSegments = (fields.dialogueSegments as DialogueSegment[] | undefined) ?? []
  const inputAssetRefs = (fields.inputAssetRefs as InputAssetRef[] | undefined) ?? []
  const dialogueAudioAssignment = (nodeData.dialogueAudioAssignment as Map<string, { ready: boolean; sourceLabel: string; nodeId: string }> | undefined) ?? new Map()
  // VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
  // §P6/§P7/§P15/§P19/§P16 — the edge-derived halves of the structured
  // asset ports + Reference Video port, computed by `CanvasBoard.tsx`
  // from the live edge graph every render (never persisted itself).
  const structuredAssetAssignment = nodeData.structuredAssetAssignment ?? new Map<string, KeyframeSlotMaterial[]>()
  const referenceVideoSlot = nodeData.referenceVideoSlot ?? null
  // VistoeraTASK-00143 §五/§七 — MULTI-slot global Audio Reference port,
  // same edge-derived-every-render convention as the two above.
  const audioReferenceSlots = nodeData.audioReferenceSlots ?? EMPTY_AUDIO_REFERENCE_SLOTS
  // Light structured metadata (spec §六) — `{ [edgeId]: role }`, additive
  // field, absent on any pre-existing Canvas (spec §十四).
  const audioAssetRoles = (fields.audioAssetRoles as Record<string, unknown> | undefined) ?? EMPTY_AUDIO_ROLE_MAP
  const audioRoleSelectOptions = useMemo(
    () =>
      VIDEO_AUDIO_ROLE_VALUES.map((value) => ({
        value,
        label: t(
          `generation.audioReferenceRole${value === 'auto' ? 'Auto' : value === 'bgm' ? 'Bgm' : value === 'voiceover' ? 'Voiceover' : 'Other'}`,
        ),
      })),
    [t],
  )

  // Pure local UI state (spec §6/§46) — selecting a stage NEVER issues an
  // API request, NEVER writes to Canvas persistence, NEVER changes the
  // board's own `selectedNodeId`/Lineage Highlight.
  const [selectedKeyframeId, setSelectedKeyframeId] = useState<string | null>(null)
  useEffect(() => {
    if (keyframeTimeline.length === 0) {
      if (selectedKeyframeId !== null) setSelectedKeyframeId(null)
      return
    }
    if (!keyframeTimeline.some((e) => e.id === selectedKeyframeId)) setSelectedKeyframeId(keyframeTimeline[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyframeTimeline])
  const selectedKeyframeEntry = keyframeTimeline.find((e) => e.id === selectedKeyframeId)

  const [assetPreview, setAssetPreview] = useState<{ url: string; label: string } | null>(null)

  const status = (fields.status as string | undefined) ?? 'READY'
  const generatedTaskId = fields.generatedTaskId as string | undefined
  const generatedVideoUrl = fields.generatedVideoUrl as string | undefined
  const hasResult = !!generatedVideoUrl
  const generatedFromFingerprint = fields.generatedFromFingerprint as string | undefined
  const failedSubmitInputFingerprint = fields.failedSubmitInputFingerprint as string | undefined
  const generatedUpstreamFingerprint = fields.generatedUpstreamFingerprint as string | undefined
  const generatedConfigFingerprint = fields.generatedConfigFingerprint as string | undefined

  const [task, setTask] = useState<VideoGenTask | null>(null)
  // VistoeraTASK-00156 §七/§八/§十 — `field` (when present) is the
  // Structured Validation Error Contract's field identifier (e.g.
  // "duration_seconds"/"quality_tier"/"aspect_ratio"/
  // "reference_audio_asset_ids"/"reference_images"), used ONLY to add a
  // visual error state to the matching control below; the full
  // actionable message ALWAYS still renders in the summary banner
  // regardless of whether `field` maps to a control here.
  const [submitError, setSubmitError] = useState<{ code?: string; message: string; field?: string } | null>(null)
  const [parameterAdjustmentHint, setParameterAdjustmentHint] = useState<string | null>(null)
  const parameterHintTimerRef = useRef<number | null>(null)
  const [config, setConfig] = useState<VideoGenConfig | null>(videoGenConfig)
  const [engines, setEngines] = useState<VideoGenEnginesResponse | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [modalStartTime, setModalStartTime] = useState(0)
  const [showMoreParams, setShowMoreParams] = useState(false)
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  // VistoeraTASK-00139 — see `shouldForceRunHydratingOnMount` docstring:
  // an already-COMPLETED/FAILED node must render its persisted result
  // immediately, never wait on the background re-verify fetch first.
  const [runHydrating, setRunHydrating] = useState(
    () => Boolean(nodeData.latestRunId) && shouldForceRunHydratingOnMount(Boolean(fields.generatedVideoUrl), status),
  )
  const pollTimerRef = useRef<number | null>(null)
  const submitFingerprintRef = useRef<string | null>(null)
  const autoGenerateConsumedRef = useRef(false)
  const runBtnRef = useRef<HTMLButtonElement | null>(null)
  const nodeShellRef = useRef<HTMLDivElement | null>(null)
  const referenceInputSectionRef = useRef<HTMLDivElement | null>(null)
  const referenceTrayRef = useRef<HTMLDivElement | null>(null)
  /** §十一 — collapsed-state handle-geometry fallback anchor (see
   * `syncInputHandles` below); never read for anything else. */
  const referenceAssetsToggleRef = useRef<HTMLButtonElement | null>(null)
  const originalPromptSectionRef = useRef<HTMLDivElement | null>(null)
  const originalPromptEditorRef = useRef<HTMLDivElement | null>(null)
  const enhancedPromptSectionRef = useRef<HTMLDivElement | null>(null)
  const keyframeGroupRef = useRef<HTMLDivElement | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )
  const submission = useCanvasGenerationSubmission({
    nodeId: id,
    fieldStatus: status,
    patchFields,
    runHydrating,
  })
  const { pinned, togglePin } = usePinnedExpandedNode(fields, patchFields)

  // VISTOERA_GENERATE_VIDEO_CANONICAL_PROVIDER_ID_AND_ADMIN_DEFAULT_
  // PROVIDER_END_TO_END_P0_V1 §P4/§P5/§P8 — a brand-new node has NO
  // persisted `providerChannel` at all (see `buildGenerateVideoNodeDefault
  // Fields`'s own docstring — never a hardcoded constant anymore). The
  // FIRST time real shared config is available, resolve the Platform
  // Admin's CURRENT canonical default Provider and persist it as this
  // node's initial selection. Once `providerChannel` is set (by this
  // effect, by a duplicate/paste carrying over a source node's value, or
  // by the tenant explicitly picking one), `explicitProvider` becomes
  // true and this effect never fires again for this node — an existing
  // node's selection is therefore never touched by a LATER Admin default
  // change (§P5 Case A/B: only NEW nodes follow a new default). Leaves
  // `providerChannel` unset (never guesses/falls back) when the Admin
  // default doesn't resolve to any currently visible channel.
  useEffect(() => {
    if (explicitProvider) return
    if (!videoGenConfig || selectableProviderChannels.length === 0) return
    const defaultChannelId = resolveDefaultProviderChannelId(videoGenConfig, selectableProviderChannels)
    const channelId =
      defaultChannelId && selectableProviderChannels.some((c) => c.id === defaultChannelId)
        ? defaultChannelId
        : selectableProviderChannels[0]?.id
    if (channelId) patchFields({ providerChannel: channelId })
  }, [explicitProvider, videoGenConfig, selectableProviderChannels, patchFields])

  useEffect(() => {
    if (providerExecutionBlocked) return
    if (submitError?.code === 'PROVIDER_EXECUTION_NOT_SUPPORTED') {
      setSubmitError(null)
    }
  }, [providerChannel, providerExecutionBlocked, submitError?.code])

  // Reference Assets Collapse (spec §九/§十) — a SEPARATE, independent
  // UI-persistence flag from `pinnedExpanded` above (never merged into
  // one state, spec §二十 "不要把 collapse 与 pin 混成一个状态"). Persisted
  // the SAME lightweight way (a `fields` boolean, no new backend
  // schema, spec §十 "不要为这个折叠状态新增后端 schema") and controls ONLY
  // the Character/Product/Scene/Other-Reference/Reference-Video body —
  // Keyframes and the Prompt/Motion/Camera sections are never affected
  // (spec §九 "关键帧不要跟着参考资产一起折叠").
  const referenceAssetsCollapsed = Boolean(fields.referenceAssetsCollapsed)
  const toggleReferenceAssetsCollapsed = useCallback(() => {
    patchFields({ referenceAssetsCollapsed: !referenceAssetsCollapsed })
  }, [patchFields, referenceAssetsCollapsed])

  const handleAddKeyframe = useCallback(() => {
    const prevIds = new Set(keyframeTimeline.map((e) => e.id))
    const next = addKeyframeEntry(keyframeTimeline, { durationSeconds })
    patchFields({ keyframeTimeline: next })
    const added = next.find((e) => !prevIds.has(e.id))
    if (added) setSelectedKeyframeId(added.id)
  }, [durationSeconds, keyframeTimeline, patchFields])

  const handleRemoveKeyframe = useCallback(
    (entryId: string) => {
      // Drop slot-aware Edge first (source node preserved), then remove the
      // timeline entry — REMOVE KEYFRAME ≠ REMOVE IMAGE, but removing the
      // keyframe itself must not leave an orphan edge into a gone slot.
      nodeData.onRemoveVideoKeyframeImage?.(id, entryId)
      const next = removeKeyframeEntry(keyframeTimeline, entryId)
      patchFields({ keyframeTimeline: next })
      if (selectedKeyframeId === entryId) setSelectedKeyframeId(next[0]?.id ?? null)
    },
    [id, keyframeTimeline, nodeData, patchFields, selectedKeyframeId],
  )

  const handleRemoveKeyframeImage = useCallback(
    (entryId: string) => {
      nodeData.onRemoveVideoKeyframeImage?.(id, entryId)
    },
    [id, nodeData],
  )

  /** VistoeraTASK-00064 P10-P15 — "清空关键帧": the ONE reversible exit
   * from Keyframe Mode back to pure text-to-video (spec §16's existing
   * "present-but-empty `keyframeTimeline` is an explicit, valid 0-
   * keyframe state" contract — never a destructive Node reset). Reuses
   * the EXACT SAME per-entry edge-disconnect (`onRemoveVideoKeyframeImage`,
   * spec §11 "REMOVE_KEYFRAME_REFERENCE ≠ DELETE_ASSET" — pure edge
   * removal, never touches the underlying image/video asset or its
   * source node) the single-keyframe "×" already uses, just looped over
   * every current entry, THEN persists `keyframeTimeline: []` so the
   * empty-array branch in the `keyframeTimeline` memo above wins even on
   * a legacy Canvas that still has live `KeyframeAssignment` edges
   * (never leaves the P12 "UI 看起来没有关键帧，但 request 仍然发送旧
   * keyframe" ghost state). Characters/Products/Scenes/Other-Reference/
   * Prompt/Provider/Skill are untouched (spec §13 — this only ever
   * patches `keyframeTimeline`, never a broader Node reset). */
  const handleClearKeyframes = useCallback(() => {
    for (const entry of keyframeTimeline) {
      nodeData.onRemoveVideoKeyframeImage?.(id, entry.id)
    }
    patchFields({ keyframeTimeline: [] })
    setSelectedKeyframeId(null)
  }, [id, keyframeTimeline, nodeData, patchFields])

  const handleClearKeyframesClick = useCallback(() => {
    if (keyframeTimeline.length === 0) return
    Modal.confirm({
      title: t('generation.keyframeClearConfirmTitle'),
      content: t('generation.keyframeClearConfirmContent'),
      okText: t('generation.keyframeClearConfirmOk'),
      cancelText: t('generation.keyframeClearConfirmCancel'),
      okButtonProps: { danger: true },
      onOk: () => handleClearKeyframes(),
    })
  }, [handleClearKeyframes, keyframeTimeline.length, t])

  const handleUpdateKeyframeEntry = useCallback(
    (entryId: string, patch: Partial<KeyframeTimelineEntry>) => {
      patchFields({ keyframeTimeline: updateKeyframeEntry(keyframeTimeline, entryId, patch) })
    },
    [keyframeTimeline, patchFields],
  )

  const handleAddDialogueSegment = useCallback(
    (atRelativeTimeSec: number) => {
      patchFields({ dialogueSegments: addDialogueSegment(dialogueSegments, atRelativeTimeSec) })
    },
    [dialogueSegments, patchFields],
  )

  // VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
  // §P15 — an edge-derived structured-asset card's synthetic
  // `edge:<edgeId>` id (see `edgeDerivedAssetRefs` below) routes removal
  // through the SAME `onDisconnectMaterial` every other Canvas edge chip
  // uses (deletes ONLY that one edge — never mutates `inputAssetRefs`,
  // since this entry was never a field to begin with). A plain
  // picker-added ref keeps the pre-existing field-mutation path
  // unchanged.
  const handleRemoveInputAssetRef = useCallback(
    (refId: string) => {
      if (refId.startsWith('edge:')) {
        // §57-parity for structured edge-derived cards — `onDisconnectMaterial`
        // already strips any Prompt mention keyed by this SAME bare edgeId
        // (VistoeraTASK-00064 P1/P3 — the mention marker for an edge-derived
        // structured asset embeds `structuredAssetMentionKey(refId)`, i.e.
        // this exact bare edgeId, never the `edge:`-prefixed display id).
        nodeData.onDisconnectMaterial?.(id, refId.slice('edge:'.length))
        return
      }
      const next = inputAssetRefs.filter((r) => r.id !== refId)
      const fp = referenceAssetsFingerprint(next)
      const hadResult = Boolean(fields.generatedTaskId || fields.generatedVideoUrl)
      // VistoeraTASK-00064 P1/P3/§57 parity — removing a manual (picker-
      // added) structured asset must also clear any inline Prompt mention
      // chip referencing it (never leave a dangling "已断开" ghost chip for
      // a deliberate removal — same rule `onDisconnectMaterial` enforces
      // for edge-derived materials/assets).
      const nextPromptText = stripMentionsForEdge(promptText, refId)
      patchFields({
        inputAssetRefs: next,
        referenceAssetsFingerprint: fp,
        ...(nextPromptText !== promptText ? { promptText: nextPromptText } : {}),
        ...(hadResult ? { referenceAssetsStale: true, needsRegeneration: true } : {}),
      })
    },
    [fields.generatedTaskId, fields.generatedVideoUrl, id, inputAssetRefs, nodeData, patchFields, promptText],
  )

  /** Converts one category/keyframe's live `character-in`/`product-in`/
   * `scene-in`/`reference-asset-in` edges into `VideoInputAssetDisplayRef`
   * display cards — merged into (never replacing) the picker-added
   * `inputAssetRefs` list both `KeyframeDetailPanel` (per-keyframe) and
   * the legacy global `VideoInputAssetsSection` branch below already
   * render (spec §P15/§P19). */
  const edgeDerivedAssetRefs = useCallback(
    (keyframeId: string | null): VideoInputAssetDisplayRef[] => {
      const categories: VideoStructuredAssetCategory[] = ['character', 'object', 'scene', 'reference']
      const out: VideoInputAssetDisplayRef[] = []
      for (const category of categories) {
        for (const slot of structuredAssetSlotsFor(structuredAssetAssignment, category, keyframeId)) {
          out.push({
            id: `edge:${slot.edgeId}`,
            category,
            label: slot.sourceLabel,
            assetId: slot.entityId ?? null,
            generatedOutputId: slot.generatedOutputId ?? null,
            sourceNodeType: slot.nodeType,
            keyframeId,
            locked: true,
            ready: slot.ready,
          })
        }
      }
      return out
    },
    [structuredAssetAssignment],
  )

  const handleAddInputAssetLabel = useCallback(
    (category: InputAssetCategory, label: string) => {
      // KEYFRAME_SCOPED_ASSETS_P0_V1 (P3/P10/P11) — a new asset always
      // binds to the CURRENTLY SELECTED keyframe, never a global list;
      // `null` only for the legacy 0-keyframe / pure text-to-video state.
      const next: InputAssetRef = {
        id: genInputAssetRefId(),
        category,
        label,
        assetId: null,
        keyframeId: selectedKeyframeEntry?.id ?? null,
      }
      const nextRefs = [...inputAssetRefs, next]
      patchFields({
        inputAssetRefs: nextRefs,
        referenceAssetsFingerprint: referenceAssetsFingerprint(nextRefs),
      })
    },
    [inputAssetRefs, patchFields, selectedKeyframeEntry],
  )

  // VistoeraTASK-00143 §六 — light, OPTIONAL structured role hint per
  // connected audio edge; keyed by `edgeId` (same identity every other
  // edge-derived chip in this file already uses), never by asset id
  // (a re-plugged asset on a NEW edge starts back at AUTO, never
  // inherits a stale role from an unrelated prior edge).
  const handleUpdateAudioAssetRole = useCallback(
    (edgeId: string, role: VideoAudioReferenceRole) => {
      patchFields({ audioAssetRoles: { ...audioAssetRoles, [edgeId]: role } })
    },
    [audioAssetRoles, patchFields],
  )

  const handlePickInputAssetImage = useCallback(
    (category: InputAssetCategory, replaceRefId?: string) => {
      nodeData.onOpenAssetPicker?.({
        nodeId: id,
        purpose: 'video_shot_reference',
        title: t('generation.shotReferencePickerTitle'),
        selectionMode: 'single',
        maxSelection: 1,
        onConfirm: (selections) => {
          const picked = selections[0]
          if (!picked?.assetId) return
          const label = picked.filename || picked.assetId.slice(0, 8)
          let nextRefs: InputAssetRef[]
          if (replaceRefId) {
            nextRefs = inputAssetRefs.map((r) =>
              r.id === replaceRefId ? { ...r, assetId: picked.assetId, label: r.label || label } : r,
            )
          } else {
            const targetKeyframeId = selectedKeyframeEntry?.id ?? null
            // §十三 "重复资产与跨槽位规则" — dedup granularity is EXACTLY
            // (node_id [implicit — this node's own `inputAssetRefs`],
            // semantic_port [category], keyframe_id, asset_id). The SAME
            // asset dropped/picked TWICE into the SAME port+keyframe is a
            // no-op (never a duplicate chip); the SAME asset picked into a
            // DIFFERENT category (or a different keyframe) is a perfectly
            // legal, independent assignment — never globally deduped by
            // asset id alone.
            const alreadyAssigned = inputAssetRefs.some(
              (r) => r.category === category && (r.keyframeId ?? null) === targetKeyframeId && r.assetId === picked.assetId,
            )
            nextRefs = alreadyAssigned
              ? inputAssetRefs
              : [
                  ...inputAssetRefs,
                  {
                    id: genInputAssetRefId(),
                    category,
                    label,
                    assetId: picked.assetId,
                    // KEYFRAME_SCOPED_ASSETS_P0_V1 — bind to the currently
                    // selected keyframe (never a global list, P3/P10/P11).
                    keyframeId: targetKeyframeId,
                  },
                ]
          }
          const hadResult = Boolean(fields.generatedTaskId || fields.generatedVideoUrl)
          const prevFp = (fields.referenceAssetsFingerprint as string | undefined) || ''
          const nextFp = referenceAssetsFingerprint(nextRefs)
          patchFields({
            inputAssetRefs: nextRefs,
            referenceAssetsFingerprint: nextFp,
            ...(hadResult && prevFp && prevFp !== nextFp
              ? { referenceAssetsStale: true, needsRegeneration: true }
              : {}),
          })
        },
      })
    },
    [fields.generatedTaskId, fields.generatedVideoUrl, fields.referenceAssetsFingerprint, id, inputAssetRefs, nodeData, patchFields, selectedKeyframeEntry, t],
  )

  /** P7/P8 — the ONE mutation path for "duration to next keyframe";
   * cascades onto every later entry (see `updateKeyframeDurationToNext`
   * — `relativeTimeSec` remains the single persisted timing source). */
  const handleUpdateKeyframeDurationToNext = useCallback(
    (entryId: string, seconds: number) => {
      patchFields({ keyframeTimeline: updateKeyframeDurationToNext(keyframeTimeline, entryId, seconds) })
    },
    [keyframeTimeline, patchFields],
  )

  // KEYFRAME_SCOPED_ASSETS_P0_V1 (P3/P10/P11) — Characters/Products/
  // Scenes/Other filtered to the CURRENTLY SELECTED keyframe only.
  // Switching keyframes swaps this list entirely; never shared state.
  const scopedInputAssetRefs = useMemo<VideoInputAssetDisplayRef[]>(
    () => [
      ...inputAssetRefsForKeyframe(inputAssetRefs, selectedKeyframeEntry?.id, keyframeTimeline),
      ...edgeDerivedAssetRefs(selectedKeyframeEntry?.id ?? null),
    ],
    [inputAssetRefs, selectedKeyframeEntry, keyframeTimeline, edgeDerivedAssetRefs],
  )

  // Legacy 0-keyframe / pure text-to-video global list (spec §16) — the
  // SAME merge, keyed by the bare (no-keyframe) `'__global__'` bucket
  // `videoStructuredAssetHandleId`/`structuredAssetAssignmentKey` both
  // use for a handle with no `::<entryId>` suffix.
  const mergedGlobalInputAssetRefs = useMemo<VideoInputAssetDisplayRef[]>(
    () => [...inputAssetRefs, ...edgeDerivedAssetRefs(null)],
    [inputAssetRefs, edgeDerivedAssetRefs],
  )

  // §九/§十 — the ONE combined "参考资产 N 项" count covering Character/
  // Product/Scene/Other-Reference (whichever scope is actually visible
  // — keyframe-scoped vs. the legacy 0-keyframe global list, same
  // discriminator the render tree below already uses) PLUS the
  // Reference Video slot (0 or 1 — spec §九 "参考视频（如果当前属于该
  // section）").
  const activeReferenceAssetRefs = keyframeTimeline.length === 0 ? mergedGlobalInputAssetRefs : scopedInputAssetRefs
  const referenceAssetsTotalCount = activeReferenceAssetRefs.length + (referenceVideoSlot ? 1 : 0)

  const handlePreviewInputAssetRef = useCallback(
    (ref: InputAssetRef) => {
      const url = resolveCanvasImageHoverPreviewUrl(workspaceId, {
        entityId: ref.assetId,
        generatedOutputId: (ref as VideoInputAssetDisplayRef).generatedOutputId,
        nodeType: (ref as VideoInputAssetDisplayRef).sourceNodeType,
      })
      if (url) setAssetPreview({ url, label: ref.label })
    },
    [workspaceId],
  )

  /** Tab body / label click — SELECT ONLY (P0_V2 §11-A/§18). Never
   * creates a node and never opens a source; pure local UI state. */
  const handleSelectKeyframe = useCallback((entryId: string) => {
    setSelectedKeyframeId(entryId)
  }, [])

  /** Image thumbnail / empty-thumb click — OPEN or CREATE source
   * (P0_V2 §11-B/§16/CASE_H-N). Distinct from tab selection so switching
   * tabs never spawns unintentional Generate Image nodes. */
  const handleOpenKeyframeImageSource = useCallback(
    (entryId: string) => {
      setSelectedKeyframeId(entryId)
      const entry = keyframeTimeline.find((e) => e.id === entryId)
      if (!entry) return
      const material = keyframeMaterialForEntry(keyframeAssignment, entry)
      const sourceType = keyframeSourceType(material)
      if (sourceType === 'GENERATED' && material?.nodeId) {
        nodeData.onFocusVideoKeyframeSourceNode?.(material.nodeId)
        return
      }
      if (material?.nodeId) {
        nodeData.onSelectNode?.(material.nodeId)
        return
      }
      nodeData.onCreateVideoKeyframeImageNode?.(id, entryId)
    },
    [id, keyframeAssignment, keyframeTimeline, nodeData],
  )

  /** "导入关键帧" (spec §17-§20) — replaces the removed global "生成图片"
   * button. Imports into the CURRENTLY selected slot; if the timeline is
   * empty, creates a START slot first then imports into it (spec §19,
   * "顺滑优先"). Never builds a second uploader — delegates entirely to
   * `onImportVideoKeyframeImage`, which reuses the same canonical
   * `ImageAssetPicker` every other Canvas node already uses (spec §20). */
  const handleImportKeyframe = useCallback(() => {
    if (selectedKeyframeEntry) {
      nodeData.onImportVideoKeyframeImage?.(id, selectedKeyframeEntry.id)
      return
    }
    const prevIds = new Set(keyframeTimeline.map((e) => e.id))
    const next = addKeyframeEntry(keyframeTimeline, { durationSeconds })
    const added = next.find((e) => !prevIds.has(e.id))
    if (!added) return
    patchFields({ keyframeTimeline: next })
    setSelectedKeyframeId(added.id)
    nodeData.onImportVideoKeyframeImage?.(id, added.id)
  }, [durationSeconds, id, keyframeTimeline, nodeData, patchFields, selectedKeyframeEntry])

  const handleCreateDialogueAudioNode = useCallback(
    (segmentId: string) => {
      nodeData.onCreateVideoDialogueAudioNode?.(id, segmentId)
    },
    [id, nodeData],
  )

  useEffect(() => {
    if (videoGenConfig) setConfig(videoGenConfig)
  }, [videoGenConfig])

  useEffect(() => {
    // P0-C (VISTOERA_CANVAS_CAPACITY_CATEGORY_A_SCALING_FIX_AND_REBASELINE_V1)
    // — this fallback only ever exists to cover the mount-order race
    // where THIS node renders before `CanvasBoard`'s own board-level
    // `videoGenerationConfig` fetch has resolved yet. It used to call
    // `getVideoGenConfig` directly, which meant N video nodes racing
    // the board's own fetch turned into N+1 real requests (measured 5x
    // on a real Preview Canvas). Routing through the single-flight
    // deduped loader means every caller — the board and every node —
    // shares exactly one in-flight request per workspace.
    if (!workspaceId || videoGenConfig) return
    let cancelled = false
    loadVideoGenConfigDeduped(workspaceId)
      .then((data) => {
        if (!cancelled) setConfig(data)
      })
      .catch(() => {
        /* Params row degrades to hardcoded fallback choices. */
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, videoGenConfig])

  useEffect(() => {
    // Same single-flight rationale as above — legacy (no
    // `providerChannel`) video nodes without an explicit provider each
    // used to independently fetch the engine catalog (measured 3x for
    // 3 legacy nodes on one Canvas). Fetched regardless of
    // `explicitProvider` (spec §34/§35) — the real `capability_matrix`
    // is needed for the Provider Capability warning banner even when an
    // explicit LOCAL_LTX/MINIMAX channel is selected; only the legacy
    // `engine` <CanvasSelect> itself is skipped in that case.
    if (!workspaceId) return
    let cancelled = false
    loadVideoGenEnginesDeduped(workspaceId)
      .then((data) => {
        if (!cancelled) setEngines(data)
      })
      .catch(() => {
        /* Capability warning banner + legacy engine selector degrade to no-op. */
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const aliasByEdgeId = useMemo(() => computeMaterialAliases(inputMaterials, t), [inputMaterials, t])
  const thumbUrlByEdgeId = useMaterialThumbMap(workspaceId, inputMaterials)

  // VistoeraTASK-00064 P1/P3/P4 — Character/Product/Scene/Other-Reference
  // structured assets are a SEPARATE identity space from the generic
  // `inputMaterials` edges above (VideoInputAssetsSection, not
  // InputMaterialsRow), but must resolve through the EXACT SAME
  // `AssetMentionPromptEditor`/`resolvePromptForSubmit` machinery so a
  // dropped structured-asset chip round-trips identically to a dropped
  // material chip (spec §3 "两者引用同一 canonical asset identity"). Built
  // across EVERY scope (global `null` + every keyframe entry — not just
  // the currently-selected one) so a mention inserted while keyframe A was
  // selected still resolves correctly after the user switches to keyframe
  // B (spec §1.6/§1.7 "保存/刷新后恢复" — resolution must never depend on
  // which keyframe tab happens to be active at render time).
  const allStructuredAssetRefs = useMemo<VideoInputAssetDisplayRef[]>(() => {
    const scopes: (string | null)[] = [null, ...keyframeTimeline.map((e) => e.id)]
    const out: VideoInputAssetDisplayRef[] = [...inputAssetRefs]
    for (const scope of scopes) out.push(...edgeDerivedAssetRefs(scope))
    return out
  }, [inputAssetRefs, keyframeTimeline, edgeDerivedAssetRefs])

  const structuredAssetAliasByRefId = useMemo(() => {
    const map = new Map<string, string>()
    for (const ref of allStructuredAssetRefs) {
      if (ref.label) map.set(structuredAssetMentionKey(ref.id), ref.label)
    }
    return map
  }, [allStructuredAssetRefs])

  const structuredAssetThumbByRefId = useMemo(() => {
    const map = new Map<string, string>()
    for (const ref of allStructuredAssetRefs) {
      if (!ref.assetId && !ref.generatedOutputId) continue
      const url = resolveCanvasImageThumbUrl(workspaceId, {
        entityId: ref.assetId,
        generatedOutputId: ref.generatedOutputId,
        nodeType: ref.sourceNodeType,
      })
      if (url) map.set(structuredAssetMentionKey(ref.id), url)
    }
    return map
  }, [allStructuredAssetRefs, workspaceId])

  // Combined (never replacing either source) — Prompt editor/resolution
  // treats a dropped Input Material chip and a dropped Structured Asset
  // chip identically (spec §3).
  const combinedAliasByRefId = useMemo(() => {
    const map = new Map(aliasByEdgeId)
    for (const [k, v] of structuredAssetAliasByRefId) map.set(k, v)
    return map
  }, [aliasByEdgeId, structuredAssetAliasByRefId])

  const combinedThumbByRefId = useMemo(() => {
    const map = new Map(thumbUrlByEdgeId)
    for (const [k, v] of structuredAssetThumbByRefId) map.set(k, v)
    return map
  }, [thumbUrlByEdgeId, structuredAssetThumbByRefId])

  const readyImageMaterials = useMemo(
    () => inputMaterials.filter((m): m is Extract<(typeof inputMaterials)[number], { kind: 'IMAGE' }> => m.kind === 'IMAGE' && m.ready),
    [inputMaterials],
  )

  const hasStartKeyframe = !!keyframeAssignment.start
  // ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4 — ALL
  // MIDDLE timeline entries, each resolved against its OWN
  // `video-kf-middle-in::<entryId>` slot (generalizes the old
  // single-MIDDLE `middle[0]` read; `keyframeAssignment.middle` itself
  // already accumulates every bound middle slot, so drift-detection via
  // `computeVideoUpstreamFingerprint` below needed NO change to already
  // cover N middles correctly).
  const middleTimelineEntries = useMemo(() => keyframeTimeline.filter((e) => e.role === 'middle'), [keyframeTimeline])
  const middleSlots = useMemo(
    () =>
      middleTimelineEntries
        .map((entry) => ({ entry, slot: keyframeMaterialForEntry(keyframeAssignment, entry) }))
        .filter((x): x is { entry: KeyframeTimelineEntry; slot: KeyframeSlotMaterial } => !!x.slot),
    [middleTimelineEntries, keyframeAssignment],
  )
  const visualConditioning = useMemo(
    () =>
      resolveVideoVisualConditioning({
        keyframeAssignment,
        keyframeTimeline,
        inputAssetRefs,
        readyImageMaterials,
        hasStartKeyframe,
      }),
    [hasStartKeyframe, inputAssetRefs, keyframeAssignment, keyframeTimeline, readyImageMaterials],
  )
  const videoOperation = useMemo(
    () =>
      resolveVideoGenerationOperation({
        hasKeyframeImage: hasStartKeyframe || visualConditioning.kind.startsWith('keyframe'),
        hasInputAssetImage: inputAssetRefs.some((ref) => Boolean(ref.assetId)),
        hasReferenceImageAsset: readyImageMaterials.length > 0,
      }),
    [hasStartKeyframe, inputAssetRefs, readyImageMaterials.length, visualConditioning.kind],
  )

  const { resolved: resolvedPromptPreview } = useMemo(
    () => resolvePromptForSubmit(promptText, combinedAliasByRefId, t),
    [promptText, combinedAliasByRefId, t],
  )
  const disconnectedMentionCount = useMemo(
    () => countDisconnectedMentions(promptText, combinedAliasByRefId),
    [promptText, combinedAliasByRefId],
  )

  const upstreamFingerprint = useMemo(
    () =>
      computeVideoUpstreamFingerprint(
        inputMaterials,
        keyframeAssignment,
        visualConditioning.inputMaterial ?? undefined,
        audioReferenceSlots,
      ),
    [audioReferenceSlots, inputMaterials, keyframeAssignment, visualConditioning.inputMaterial],
  )

  const keyframeContextFingerprint = useMemo(
    () => JSON.stringify({ keyframeTimeline, dialogueSegments, inputAssetRefs }),
    [dialogueSegments, inputAssetRefs, keyframeTimeline],
  )

  const configFingerprint = useMemo(
    () =>
      computeVideoConfigFingerprint({
        prompt: resolvedPromptPreview,
        ratio,
        qualityTier,
        durationSeconds,
        providerChannel: providerChannel ?? null,
        engine,
        middleTimePosition,
        keyframeContext: keyframeContextFingerprint,
      }),
    [durationSeconds, engine, keyframeContextFingerprint, middleTimePosition, providerChannel, qualityTier, ratio, resolvedPromptPreview],
  )

  // Provider Capability Adapter (spec §34-§36) — never silently drops:
  // exactly which capabilities the CURRENT timeline/dialogue-audio state
  // needs vs. what the selected engine's REAL `capability_matrix`
  // (`GET /video-generation/engines`) reports.
  const hasAnyReadyDialogueAudio = useMemo(
    () => Array.from(dialogueAudioAssignment.values()).some((v) => v.ready),
    [dialogueAudioAssignment],
  )
  const requiredCapabilities = useMemo(
    () =>
      computeRequiredCapabilities(keyframeTimeline, hasAnyReadyDialogueAudio, {
        hasVisualReferenceInput: hasVisualConditioningInput({
          keyframeTimeline,
          inputAssetRefs,
          readyImageMaterials,
        }),
      }),
    [hasAnyReadyDialogueAudio, inputAssetRefs, keyframeTimeline, readyImageMaterials],
  )
  const capabilityEngineKey = useMemo(
    () => resolveCapabilityEngineKey(selectedProvider, explicitProvider ? null : engine),
    [engine, explicitProvider, selectedProvider],
  )
  const engineCapabilities = capabilityEngineKey ? engines?.capability_matrix?.[capabilityEngineKey]?.capabilities : undefined
  // VistoeraTASK-00178 §二/§十 — real, distinct structured Character/
  // Product/Scene/Other-Reference chips currently connected (same
  // ready/entityId-or-generatedOutputId filter the submit handler's
  // `structuredReferenceImageSlots` uses below), deduplicated by
  // resolved id — mirrors submission-time materialization exactly so
  // this warning never disagrees with what actually gets submitted.
  const structuredReferenceImageConnectedCount = useMemo(() => {
    const categories: VideoStructuredAssetCategory[] = ['character', 'object', 'scene', 'reference']
    const ids = new Set<string>()
    for (const category of categories) {
      for (const slot of structuredAssetSlotsFor(structuredAssetAssignment, category, selectedKeyframeEntry?.id ?? null)) {
        if (slot.ready && (slot.entityId || slot.generatedOutputId)) {
          ids.add(String(slot.entityId || slot.generatedOutputId))
        }
      }
    }
    return ids.size
  }, [selectedKeyframeEntry, structuredAssetAssignment])
  const structuredReferenceWarning = useMemo(
    () => computeStructuredReferenceCapabilityWarning(structuredReferenceImageConnectedCount, selectedProvider?.input_capability),
    [selectedProvider, structuredReferenceImageConnectedCount],
  )
  const capabilityWarnings = useMemo(() => {
    const base = computeCapabilityWarnings(requiredCapabilities, engineCapabilities)
    return structuredReferenceWarning ? [...base, structuredReferenceWarning] : base
  }, [engineCapabilities, requiredCapabilities, structuredReferenceWarning])

  const inputFingerprint = useMemo(
    () =>
      JSON.stringify({
        upstream: upstreamFingerprint,
        config: configFingerprint,
      }),
    [configFingerprint, upstreamFingerprint],
  )

  const driftState = useMemo(
    () =>
      computeVideoGenerationDriftState({
        hasResult,
        needsRegenerationFlag: Boolean(fields.needsRegeneration),
        generatedUpstreamFingerprint,
        generatedConfigFingerprint,
        generatedFromFingerprint,
        currentUpstreamFingerprint: upstreamFingerprint,
        currentConfigFingerprint: configFingerprint,
      }),
    [
      configFingerprint,
      fields.needsRegeneration,
      generatedConfigFingerprint,
      generatedFromFingerprint,
      generatedUpstreamFingerprint,
      hasResult,
      upstreamFingerprint,
    ],
  )

  const driftBannerText = generationDriftBannerText(driftState, t)
  const driftInspectorText = generationDriftInspectorText(driftState, t)

  const showActivePreview = shouldShowActiveGenerationOutput(
    generatedVideoUrl,
    status,
    { hasExistingResult: hasResult, runHydrating, latestRunId: nodeData.latestRunId },
  )
  const isBusy = submission.isBusy
  const showGeneratingPlaceholder = isBusy && !showActivePreview
  const controlsLocked = isBusy
  const isStoryboardBound = Boolean(fields.boundOrchestratorNodeId || fields.boundVideoProductionNodeId)
  const aspectRatioLocked = Boolean(isStoryboardBound || fields.aspectRatioLocked)
  const resolutionLocked = Boolean(fields.resolutionLocked)
  const formatControlsLocked = controlsLocked
  const lockedFormatSummary = fields.lockedProductionFormat as
    | { aspect_ratio?: string; resolution?: string; version?: number }
    | null
    | undefined
  const productionTargetWidth = fields.productionTargetWidth as number | null | undefined
  const productionTargetHeight = fields.productionTargetHeight as number | null | undefined
  const formatMismatch = (fields.formatCompatibility as string | undefined) === 'format_mismatch'
  const deliveryRenderStale = Boolean(fields.deliveryRenderStale) && !formatMismatch
  const formatSourceLabel = isStoryboardBound ? '来自：生成分镜' : null

  const applyStandaloneFormat = useCallback(
    (patch: { aspect_ratio?: ProductionAspectRatio; resolution?: ProductionResolution }) => {
      if (isStoryboardBound) return
      const current = (fields.nodeProductionFormat || fields.lockedProductionFormat || {
        aspect_ratio: normalizeProductionAspectRatio(ratio) || recommendedProductionFormat().aspect_ratio,
        resolution: normalizeProductionResolution(qualityTier) || recommendedProductionFormat().resolution,
        version: typeof fields.productionFormatVersion === 'number' ? fields.productionFormatVersion : 1,
      }) as { aspect_ratio: ProductionAspectRatio; resolution: ProductionResolution; version: number }
      const { next, versionBumped, impact } = applyProductionFormatChange(current, patch)
      const hasMedia = Boolean(fields.generatedVideoUrl) || Boolean(keyframeAssignment.start?.ready || keyframeAssignment.end?.ready)
      const commit = () => {
        nodeData.onPropagateVideoNodeProductionFormat?.(id, next, {
          impact: versionBumped ? impact : undefined,
        })
        if (!nodeData.onPropagateVideoNodeProductionFormat) {
          patchFields(buildStandaloneVideoFormatFields(next))
        }
      }
      if (versionBumped && hasMedia) {
        Modal.confirm({
          title: '修改成片规格？',
          content: '修改后，本视频节点已生成的关键帧和视频将不再匹配新规格，需要重新生成。是否继续？',
          okText: '修改并使已有结果失效',
          cancelText: '取消',
          onOk: () => commit(),
        })
        return
      }
      commit()
    },
    [
      fields.generatedVideoUrl,
      fields.lockedProductionFormat,
      fields.nodeProductionFormat,
      fields.productionFormatVersion,
      id,
      isStoryboardBound,
      keyframeAssignment.end?.ready,
      keyframeAssignment.start?.ready,
      nodeData,
      patchFields,
      qualityTier,
      ratio,
    ],
  )

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const handleOrphanedRun = useCallback(() => {
    stopPolling()
    setTask(null)
    nodeData.onChangeLatestRunId?.(id, null)
    patchFields({ status: hasResult ? 'COMPLETED' : 'READY', needsRegeneration: false, generatedTaskId: undefined })
    setSubmitError({ message: t('generation.errorPreviousStale') })
    submitFingerprintRef.current = null
  }, [hasResult, id, nodeData, patchFields, stopPolling, t])

  const applyCompletedTask = useCallback(
    (next: VideoGenTask) => {
      setTask(next)
      if (next.status !== 'succeeded' || !next.output_asset_id) {
        const failedFingerprint = submitFingerprintRef.current || inputFingerprint
        patchFields({
          status: resolveCanvasVideoTaskFieldStatus(next),
          generatedTaskId: next.id,
          previousOutputId: undefined,
          ...(next.status === 'failed'
            ? { failedSubmitInputFingerprint: failedFingerprint }
            : { failedSubmitInputFingerprint: null }),
        })
        if (next.status === 'failed') submitFingerprintRef.current = null
        return
      }
      // Race guard — discard late results if the upstream input changed
      // after this task was submitted (mirrors `GenerateImageNode`).
      if (submitFingerprintRef.current && submitFingerprintRef.current !== inputFingerprint) {
        patchFields({ status: hasResult ? 'COMPLETED' : 'READY', needsRegeneration: true, generatedTaskId: next.id })
        submitFingerprintRef.current = null
        return
      }
      const capturedFingerprint = submitFingerprintRef.current || inputFingerprint
      submitFingerprintRef.current = null
      patchFields({
        status: 'COMPLETED',
        generatedTaskId: next.id,
        generatedVideoUrl: videoGenTaskOutputUrl(workspaceId, next.id),
        formatCompatibility: 'ok',
        generatedAgainstFormatVersion:
          typeof fields.generatedAgainstFormatVersion === 'number'
            ? fields.generatedAgainstFormatVersion
            : typeof (fields.lockedProductionFormat as { version?: number } | null)?.version === 'number'
              ? (fields.lockedProductionFormat as { version: number }).version
              : fields.generatedAgainstFormatVersion ?? null,
        previousOutputId: undefined,
        generatedUpstreamFingerprint: upstreamFingerprint,
        generatedConfigFingerprint: configFingerprint,
        generatedFromFingerprint: capturedFingerprint,
        needsRegeneration: false,
        failedSubmitInputFingerprint: null,
      })
    },
    [hasResult, inputFingerprint, patchFields, upstreamFingerprint, configFingerprint, workspaceId, fields.generatedAgainstFormatVersion, fields.lockedProductionFormat],
  )

  const pollOnce = useCallback(
    (taskId: string) => {
      getVideoGenTask(workspaceId, taskId)
        .then(({ data: next }) => {
          if (!isTerminalStatus(next.status)) {
            setTask(next)
            patchFields({ status: resolveCanvasVideoTaskFieldStatus(next), generatedTaskId: next.id })
            pollTimerRef.current = window.setTimeout(() => pollOnce(taskId), POLL_INTERVAL_MS)
          } else {
            applyCompletedTask(next)
          }
        })
        .catch((err: unknown) => {
          if (isVideoTaskNotFound(err)) {
            handleOrphanedRun()
            return
          }
          pollTimerRef.current = window.setTimeout(() => pollOnce(taskId), POLL_INTERVAL_MS)
        })
    },
    [applyCompletedTask, handleOrphanedRun, patchFields, workspaceId],
  )

  useEffect(() => {
    stopPolling()
    setTask(null)
    if (!nodeData.latestRunId || !workspaceId) {
      setRunHydrating(false)
      return
    }
    if (shouldForceRunHydratingOnMount(hasResult, status)) setRunHydrating(true)
    getVideoGenTask(workspaceId, nodeData.latestRunId)
      .then(({ data: next }) => {
        if (!isTerminalStatus(next.status)) {
          setTask(next)
          patchFields({ status: resolveCanvasVideoTaskFieldStatus(next), generatedTaskId: next.id })
          pollTimerRef.current = window.setTimeout(() => pollOnce(next.id), POLL_INTERVAL_MS)
        } else if (!hasResult) {
          applyCompletedTask(next)
        } else {
          setTask(next)
        }
      })
      .catch((err: unknown) => {
        if (isVideoTaskNotFound(err)) {
          handleOrphanedRun()
        }
      })
      .finally(() => setRunHydrating(false))
    return () => stopPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData.latestRunId, workspaceId])

  useEffect(() => stopPolling, [stopPolling])

  const runTask = useCallback(() => {
    if (isBusy || submission.submitLockRef.current) return
    if (!submission.beginSubmission()) return
    const { effectivePrompt, effectivePromptSource: promptSource, originalPrompt } =
      resolveEffectiveVideoPrompt(fields)
    const { resolved: resolvedPrompt, hasDisconnectedMention } = resolvePromptForSubmit(
      effectivePrompt,
      combinedAliasByRefId,
      t,
    )
    if (hasDisconnectedMention) {
      submission.failSubmission()
      setSubmitError({ message: t('generation.errorDisconnectedMentions') })
      return
    }
    const keyframeSlots = [keyframeAssignment.start, ...middleSlots.map((m) => m.slot), keyframeAssignment.end].filter(Boolean) as KeyframeSlotMaterial[]
    // Explicit Target Input model
    // (VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
    // §一/§十五) — the single scalar `reference_image_asset_id` fallback
    // slot must consider the SAME edge-derived Character/Product/
    // Scene/Other-Reference assignments already rendered as "connected"
    // chips (`scopedInputAssetRefs`), never just the picker-added
    // subset — otherwise a chip the user sees as connected could
    // silently never reach the real generation request (§十五 "不要只
    // 证明 UI 正确").
    const visualConditioningRefs: InputAssetRef[] = scopedInputAssetRefs
    const visual = resolveVideoVisualConditioning({
      keyframeAssignment,
      keyframeTimeline,
      inputAssetRefs: visualConditioningRefs,
      readyImageMaterials,
      hasStartKeyframe,
    })
    const visualDiagnostics = buildVisualConditioningDiagnostics({
      resolved: visual,
      inputAssetRefs: visualConditioningRefs,
      effectivePromptSource: promptSource,
    })
    // §十五 "Generation Payload 必须最终验证" — preserve the FULL
    // structured breakdown (not just the one scalar fallback image
    // above) into `source_context`, honestly scoped (see
    // `buildStructuredAssetContextSnapshot`'s own docstring — diagnostic
    // preservation, not yet a second execution path).
    const structuredAssetContext = buildStructuredAssetContextSnapshot(
      structuredAssetAssignment,
      selectedKeyframeEntry?.id ?? null,
      referenceVideoSlot,
      audioReferenceSlots,
      audioAssetRoles,
    )
    const notReady =
      keyframeSlots.some((s) => !s.ready) ||
      (visual.kind === 'input_material' && visual.inputMaterial && !visual.inputMaterial.ready)
    if (notReady) {
      submission.failSubmission()
      setSubmitError({ message: t('generation.errorMaterialsNotReady') })
      return
    }
    // VistoeraTASK-00143 §八/§九 — an audio reference is connected but the
    // CURRENTLY selected Provider channel does not declare audio-input
    // support: explicit, submit-blocking validation. Never silently
    // discard the binding, never auto-remove it, never auto-switch
    // Provider, never claim it was actually sent (spec §九 hard rule).
    if (audioReferenceSlots.length > 0 && !audioInputSupportedBySelectedProvider) {
      submission.failSubmission()
      setSubmitError({ message: t('generation.errorAudioReferenceUnsupportedByProvider') })
      return
    }
    submitFingerprintRef.current = inputFingerprint
    setSubmitError(null)
    patchFields({
      lastSubmittedGenerationParameters: {
        prompt: resolvedPrompt.trim(),
        ratio,
        qualityTier,
        durationSeconds,
        providerChannel: providerChannel ?? null,
        engine: explicitProvider ? null : engine,
      },
    })

    const keyframeTasks: Promise<VideoGenKeyframeItem>[] = []
    const missingDataSourceMessage = t('generation.errorMissingDataSource')
    if (keyframeAssignment.start) {
      const startSlot = keyframeAssignment.start
      keyframeTasks.push(
        materializeImageSource(workspaceId, startSlot, missingDataSourceMessage).then((asset_id) => ({ role: 'start_image' as VideoKeyframeRole, asset_id })),
      )
    }
    // ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_P0_V1 §3/§4 — one
    // `middle_image` task per bound MIDDLE timeline entry (generalizes
    // the old single-MIDDLE submit; each entry's OWN shot-relative
    // `relativeTimeSec`, spec §13, is the `time_position` sent — never
    // the legacy scalar `middleTimePosition` once a real entry exists).
    for (const { entry, slot } of middleSlots) {
      keyframeTasks.push(
        materializeImageSource(workspaceId, slot, missingDataSourceMessage).then((asset_id) => ({
          role: 'middle_image' as VideoKeyframeRole,
          asset_id,
          time_position: entry.relativeTimeSec,
        })),
      )
    }
    if (keyframeAssignment.end) {
      const endSlot = keyframeAssignment.end
      keyframeTasks.push(
        materializeImageSource(workspaceId, endSlot, missingDataSourceMessage).then((asset_id) => ({ role: 'end_image' as VideoKeyframeRole, asset_id })),
      )
    }

    const structuredRefImageSlots =
      keyframeAssignment.start || visual.kind === 'keyframe_start' || visual.kind === 'keyframe_timeline_start'
        ? []
        : prioritizeRefsForKeyframe(visualConditioningRefs, 9)
    const referenceImagePromises = structuredRefImageSlots.map((ref) =>
      materializeSiteAssetRef(workspaceId, ref.assetId as string),
    )
    const legacyReferenceImagePromise =
      referenceImagePromises.length > 0
        ? Promise.resolve(undefined)
        : visual.kind === 'input_asset_ref' && visual.inputAssetRef?.assetId
          ? materializeSiteAssetRef(workspaceId, visual.inputAssetRef.assetId)
          : visual.kind === 'input_material' && visual.inputMaterial
            ? materializeImageSource(workspaceId, visual.inputMaterial, missingDataSourceMessage)
            : Promise.resolve(undefined)

    // VistoeraTASK-00157 §九/§十四/§十八 — materialize EVERY ready
    // structured Character/Product/Scene/Other-Reference chip (not just
    // the single scalar `visual` pick above) into real reference image
    // asset ids, so Seedance 2.5's now-genuine N-image capability
    // actually receives all of them. Before this task only ONE of these
    // chips ever reached `reference_image_asset_id` — the rest were
    // diagnostic-only inside `source_context.structured_asset_context`
    // (see that field's own "HONEST SCOPE" docstring in `dataflow.ts`,
    // which this fix supersedes). The backend dedups by `asset_id`
    // against the singular/keyframe fields (Multi-Role Asset Policy —
    // see `create_task`'s own docstring in `video_generation_service.
    // py`), so overlap with `referenceImagePromise` above is safe and
    // never double-submitted.
    const structuredReferenceImageCategories: VideoStructuredAssetCategory[] = ['character', 'object', 'scene', 'reference']
    // VistoeraTASK-00197 — `structuredAssetSlotsFor` returns
    // `KeyframeSlotMaterial[]`, which does NOT (and must not) carry a
    // `category` field — a keyframe slot's category is only known at
    // THIS call site (the loop below), never on the slot object itself
    // (see `KeyframeSlotMaterial`'s own docstring in `dataflow.ts`: it
    // models keyframe TIME/state, never identity/semantic-conditioning
    // role). The previous `.flatMap` collapsed every category's slots
    // into one flat `KeyframeSlotMaterial[]` and then tried to read a
    // non-existent `.category` back off it below — a real TS2339 build
    // error, not a type-annotation gap. Fixed by tracking the category
    // in a SEPARATE, same-length/same-order array instead of inventing
    // a fake field on `KeyframeSlotMaterial`.
    const structuredReferenceImageSlots: KeyframeSlotMaterial[] = []
    const structuredReferenceImageSlotCategories: VideoStructuredAssetCategory[] = []
    for (const category of structuredReferenceImageCategories) {
      for (const slot of structuredAssetSlotsFor(structuredAssetAssignment, category, selectedKeyframeEntry?.id ?? null)) {
        if (slot.ready && (Boolean(slot.entityId) || Boolean(slot.generatedOutputId))) {
          structuredReferenceImageSlots.push(slot)
          structuredReferenceImageSlotCategories.push(category)
        }
      }
    }
    const structuredReferenceImageAssetIdsPromise = Promise.all(
      structuredReferenceImageSlots.map((slot) => materializeImageSource(workspaceId, slot, missingDataSourceMessage)),
    )

    // VistoeraTASK-00150 §四/§六/§十二 — a structured audio reference
    // slot's `entityId` is a Canvas `SiteAsset.id` (`audio_asset` node)
    // or an already-generated `generate_voice` output asset id, NEITHER
    // of which is a `VideoGenerationAsset` the `/tasks` endpoint accepts
    // directly (same "Canvas Materialization Bridge" gap
    // `referenceImagePromise` above already solves for images) —
    // `materializeSiteAssetRef` reuses the EXACT SAME `kind: 'site_asset'`
    // bridge call; the backend's own `materialize_site_asset` is now
    // source-media-type-aware (`audio` -> a real, correctly-tagged
    // `VideoGenerationAsset`), never a second parallel audio-materialize
    // endpoint. A slot the UI shows as "connected" but that never
    // resolved a real `entityId` (e.g. a legacy label-only `voice`/
    // `music_sfx` entity node) is filtered out here rather than sent as
    // a doomed id — never silently claimed as submitted either (spec
    // §十五 "不要只证明 UI 正确").
    const audioAssetPromises = audioReferenceSlots
      .filter((slot) => slot.ready && Boolean(slot.entityId))
      .map((slot) => materializeSiteAssetRef(workspaceId, slot.entityId as string))

    Promise.all([
      Promise.all(keyframeTasks),
      Promise.all(referenceImagePromises),
      legacyReferenceImagePromise,
      Promise.all(audioAssetPromises),
      structuredReferenceImageAssetIdsPromise,
    ])
      .then(
        ([
          keyframes,
          referenceImageAssetIds,
          legacyReferenceImageAssetId,
          referenceAudioAssetIds,
          structuredReferenceImageAssetIds,
        ]) => {
        // VistoeraTASK-00197 §五 — canonical collect->dedupe->stable-order
        // pass, extracted to `mergeCanonicalReferenceImageAssetIds` so it
        // is a single, reusable, unit-tested implementation instead of
        // ad hoc inline `Set`/dedupe logic duplicated per caller.
        const mergedReferenceImageIds = mergeCanonicalReferenceImageAssetIds(
          referenceImageAssetIds as Array<string | undefined | null>,
          legacyReferenceImageAssetId as string | undefined | null,
          structuredReferenceImageAssetIds as Array<string | undefined | null>,
        )
        const referenceImageAssetId = mergedReferenceImageIds[0] ?? null
        const visualReferenceIds = (referenceImageAssetIds.filter(Boolean) as string[])
        submission.markRequestStart()
        return createVideoGenTask(
          workspaceId,
          buildVideoGenerationSubmitPayload({
            channel: selectedProvider,
            modelIdOverride: modelChoices.length > 1 ? selectedModelId : undefined,
            prompt: resolvedPrompt.trim(),
            aspect_ratio: ratio,
            quality_tier: qualityTier,
            duration_seconds: durationSeconds,
            seed_mode: 'random',
            enhance_prompt: false,
            reference_image_asset_id: referenceImageAssetId || null,
            reference_image_asset_ids:
              mergedReferenceImageIds.length > 0 ? mergedReferenceImageIds : null,
            reference_image_bindings:
              mergedReferenceImageIds.length > 0
                ? mergedReferenceImageIds.map((assetId, idx) => {
                    let canvas_role = 'reference'
                    const visIdx = visualReferenceIds.indexOf(assetId)
                    if (visIdx >= 0 && structuredRefImageSlots[visIdx]) {
                      canvas_role = structuredRefImageSlots[visIdx].category ?? 'reference'
                    } else {
                      const structIdx = structuredReferenceImageAssetIds.indexOf(assetId)
                      if (structIdx >= 0 && structuredReferenceImageSlotCategories[structIdx]) {
                        canvas_role = structuredReferenceImageSlotCategories[structIdx] ?? 'reference'
                      }
                    }
                    return { asset_id: assetId, canvas_role, ordinal: idx + 1 }
                  })
                : null,
            // Reference Video Port Foundation (spec §P16/§P17) — only an
            // upload-sourced `video_asset` slot is `ready` (its `entityId`
            // is ALREADY the target `VideoGenerationAsset.id`, no
            // materialize call needed, see `buildVideoReferenceVideoAssignment`'s
            // own docstring). Job/composite-sourced `video_asset` and any
            // `generate_video` slot stay `null` here — never submitted —
            // because no backend bridge resolves those to a valid id yet.
            reference_video_asset_id: referenceVideoSlot?.ready ? referenceVideoSlot.entityId ?? null : null,
            reference_audio_asset_ids: referenceAudioAssetIds.length > 0 ? referenceAudioAssetIds : null,
            keyframes: keyframes.length > 0 ? keyframes : undefined,
            source_context: {
              source: 'production_canvas',
              node_id: id,
              project_id: projectId || undefined,
              original_prompt_snapshot: originalPrompt,
              effective_prompt_snapshot: resolvedPrompt.trim(),
              effective_prompt_source: promptSource,
              prompt_enhancement_run_id: (fields.promptEnhancementRunId as string | null) ?? null,
              prompt_enhancement_skill_id: (fields.promptEnhancementSkillId as string | null) ?? null,
              prompt_enhancement_skill_version: (fields.promptEnhancementSkillVersion as string | null) ?? null,
              visual_conditioning: visualDiagnostics,
              structured_asset_context: structuredAssetContext,
            },
            engine: explicitProvider ? null : engine,
          }),
        )
      })
      .then(({ data: created }) => {
        submission.markJobAccepted()
        nodeData.onChangeLatestRunId?.(id, created.id)
        setTask(created)
        const acceptedPatch: Record<string, unknown> = {
          status: resolveCanvasVideoTaskFieldStatus(created),
          generatedTaskId: created.id,
        }
        if (generatedVideoUrl) {
          acceptedPatch.previousOutputId = generatedTaskId
        }
        if (!isTerminalStatus(created.status)) {
          patchFields(acceptedPatch)
          pollTimerRef.current = window.setTimeout(() => pollOnce(created.id), POLL_INTERVAL_MS)
        } else {
          patchFields(acceptedPatch)
          applyCompletedTask(created)
        }
      })
      .catch((err: unknown) => {
        submitFingerprintRef.current = null
        const apiErr = (err || {}) as SaasApiError
        const localized = localizeVideoGenerationSubmitError(apiErr, t)
        if (isVideoEngineStartingError(apiErr.code)) {
          submission.cancelSubmission()
          patchFields({ status: 'WAITING_FOR_ENGINE' })
          setSubmitError({ code: apiErr.code, message: localized, field: apiErr.field })
          return
        }
        submission.failSubmission({ status: hasResult ? 'COMPLETED' : 'READY' })
        setSubmitError({ code: apiErr.code, message: localized, field: apiErr.field })
      })
  }, [
    combinedAliasByRefId,
    applyCompletedTask,
    durationSeconds,
    engine,
    explicitProvider,
    fields,
    generatedTaskId,
    generatedVideoUrl,
    hasResult,
    hasStartKeyframe,
    id,
    inputAssetRefs,
    inputFingerprint,
    isBusy,
    keyframeAssignment,
    keyframeAssignment.end,
    keyframeAssignment.start,
    keyframeTimeline,
    middleSlots,
    readyImageMaterials,
    nodeData,
    patchFields,
    pollOnce,
    providerChannel,
    qualityTier,
    ratio,
    referenceVideoSlot,
    scopedInputAssetRefs,
    selectedKeyframeEntry,
    selectedProvider,
    structuredAssetAssignment,
    t,
    workspaceId,
    submission,
  ])

  const handleGenerateClick = useCallback(() => {
    if (isBusy || submission.submitLockRef.current) return
    const { effectivePrompt } = resolveEffectiveVideoPrompt(fields)
    if (!effectivePrompt.trim()) {
      message.warning(t('generation.promptRequiredVideo'))
      return
    }
    if (hasResult) {
      setRegenerateChoiceOpen(true)
      return
    }
    runTask()
  }, [fields, hasResult, isBusy, runTask, submission.submitLockRef, t])

  const handleOverwriteRegenerate = useCallback(() => {
    setRegenerateChoiceOpen(false)
    runTask()
  }, [runTask])

  const handleRegenerateSeparately = useCallback(() => {
    setRegenerateChoiceOpen(false)
    nodeData.onDuplicateForRegenerate?.(id)
  }, [id, nodeData])

  useEffect(() => {
    if (!fields.pendingAutoGenerate || isBusy || autoGenerateConsumedRef.current) return
    autoGenerateConsumedRef.current = true
    patchFields({ pendingAutoGenerate: false })
    const { effectivePrompt } = resolveEffectiveVideoPrompt(fields)
    if (!effectivePrompt.trim()) return
    runTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.pendingAutoGenerate, isBusy])

  const engineOptions = useMemo(() => {
    const choices = engines?.choices || [{ value: 'auto', label: t('generation.engineAuto'), available: true }]
    return choices.map((c) => ({
      value: c.value,
      label: c.available ? c.label : `${c.label}${t('generation.engineUnavailableSuffix')}`,
    }))
  }, [engines, t])

  const handleDownload = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!generatedTaskId) return
      downloadCanvasMedia(videoGenTaskOutputUrl(workspaceId, generatedTaskId))
    },
    [generatedTaskId, workspaceId],
  )

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const videoPreviewRef = useRef<CanvasVideoPreviewHandle>(null)

  const { onClick: onMediaSingleClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      // V2: single-click video → expand (no toggle) + sticky playback.
      nodeData.onExpandFunctionCardDetail?.(id)
      videoPreviewRef.current?.activateStickyPlayback()
    },
    () => {
      // Handoff: capture canvas time, open modal (coordinator claim happens inside MediaLightbox).
      const assetKey = generatedVideoUrl || generatedTaskId || id
      setModalStartTime(getPlaybackTime(assetKey))
      setPreviewOpen(true)
    },
  )

  const isDetailExpanded = !!nodeData.isDetailExpanded
  const showInspector = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)
  const stickyPlayback = isStickyMediaPlayback(isDetailExpanded, nodeData.isDragTempCollapsed)

  const posterUrl = generatedTaskId ? videoGenTaskPosterUrl(workspaceId, generatedTaskId) : undefined

  const runLabel = useMemo(
    () =>
      generationFooterRunLabel(submission.effectiveStatus, t, {
        hasResult,
        media: 'video',
      }),
    [hasResult, submission.effectiveStatus, t],
  )

  const pendingPlaceholderText = useMemo(() => {
    if (status === 'QUEUED' && task?.queue_position != null) {
      return t('generation.statusQueuedPosition', { position: task.queue_position })
    }
    return generationResultPlaceholderLabel(submission.effectiveStatus, t, {
      runHydrating,
      showActivePreview: false,
    })
  }, [runHydrating, status, submission.effectiveStatus, t, task?.queue_position])

  const ratioOptions = channelRatioChoices(selectedProvider, config?.aspect_ratio_choices?.length ? config.aspect_ratio_choices : FALLBACK_RATIOS).map(
    (v) => ({ value: v, label: v }),
  )
  const durationOptions = channelDurationChoices(
    selectedProvider,
    config?.duration_preset_choices?.length ? config.duration_preset_choices : FALLBACK_DURATIONS,
    qualityTier,
  ).map((v) => ({ value: v, label: `${v}s` }))
  const qualityOptions = channelQualityChoices(
    selectedProvider,
    config?.quality_tier_choices?.length ? config.quality_tier_choices : videoQualityOptions,
  )

  const parameterFallbackRatios = config?.aspect_ratio_choices?.length ? config.aspect_ratio_choices : FALLBACK_RATIOS
  const parameterFallbackDurations = config?.duration_preset_choices?.length ? config.duration_preset_choices : FALLBACK_DURATIONS
  const parameterFallbackQuality = config?.quality_tier_choices?.length ? config.quality_tier_choices : videoQualityOptions

  const showParameterAdjustmentHint = useCallback(
    (adjustments: ReturnType<typeof normalizeVideoGenerationParameters>['adjustments']) => {
      const hint = formatParameterAdjustmentHint(adjustments, t)
      if (!hint) return
      setParameterAdjustmentHint(hint)
      if (parameterHintTimerRef.current) window.clearTimeout(parameterHintTimerRef.current)
      parameterHintTimerRef.current = window.setTimeout(() => setParameterAdjustmentHint(null), 6000)
    },
    [t],
  )

  /** P12-P16 — deterministic provider/operation reconciliation against
   * backend-owned channel capability choices (never Canvas-hardcoded). */
  const reconcileVideoParameters = useCallback(
    (channel: ReturnType<typeof resolveProviderChannel>, showHint = false) => {
      const normalized = normalizeVideoGenerationParameters({
        channel,
        operation: videoOperation,
        aspectRatio: ratio,
        durationSeconds,
        qualityTier,
        fallbackRatios: parameterFallbackRatios,
        fallbackDurations: parameterFallbackDurations,
        fallbackQuality: parameterFallbackQuality,
      })
      const changed =
        normalized.aspectRatio !== ratio ||
        normalized.durationSeconds !== durationSeconds ||
        normalized.qualityTier !== qualityTier
      if (!changed) return
      patchFields({
        ratio: normalized.aspectRatio,
        durationSeconds: normalized.durationSeconds,
        qualityTier: normalized.qualityTier,
      })
      if (showHint) showParameterAdjustmentHint(normalized.adjustments)
    },
    [
      durationSeconds,
      parameterFallbackDurations,
      parameterFallbackQuality,
      parameterFallbackRatios,
      patchFields,
      qualityTier,
      ratio,
      showParameterAdjustmentHint,
      videoOperation,
    ],
  )

  useEffect(() => {
    if (!selectedProvider) return
    reconcileVideoParameters(selectedProvider, false)
  }, [reconcileVideoParameters, selectedProvider, selectedModelId, videoOperation, qualityTier])

  useEffect(
    () => () => {
      if (parameterHintTimerRef.current) window.clearTimeout(parameterHintTimerRef.current)
    },
    [],
  )

  const handleProviderChange = useCallback(
    (channelId: string) => {
      setSubmitError(null)
      const channel = providerChannels.find((c) => c.id === channelId)
      patchFields({
        providerChannel: channelId,
        modelId: channel?.model_id,
      })
      const normalized = normalizeVideoGenerationParameters({
        channel: channel ?? null,
        operation: videoOperation,
        aspectRatio: ratio,
        durationSeconds,
        qualityTier,
        fallbackRatios: parameterFallbackRatios,
        fallbackDurations: parameterFallbackDurations,
        fallbackQuality: parameterFallbackQuality,
      })
      patchFields({
        providerChannel: channelId,
        modelId: channel?.model_id,
        ratio: normalized.aspectRatio,
        durationSeconds: normalized.durationSeconds,
        qualityTier: normalized.qualityTier,
      })
      showParameterAdjustmentHint(normalized.adjustments)
    },
    [
      durationSeconds,
      parameterFallbackDurations,
      parameterFallbackQuality,
      parameterFallbackRatios,
      patchFields,
      providerChannels,
      qualityTier,
      ratio,
      showParameterAdjustmentHint,
      videoOperation,
    ],
  )

  const previewBox = computePreviewBox(ratio)
  const nodeWidth = useMemo(
    () =>
      resolveGenerationCardWidth({
        expanded: showInspector,
        expandedWidthPx: VIDEO_CARD_WIDTH,
        hasGeneratedResult: showActivePreview,
        strategy: 'video',
        aspectRatio: ratio,
        resultCarrierWidthPx: !showInspector && showActivePreview ? previewBox.width : undefined,
      }),
    [previewBox.width, ratio, showActivePreview, showInspector],
  )
  const mediaAreaHeight = showInspector
    ? computeExpandedPreviewHeight(ratio, VIDEO_CARD_WIDTH)
    : previewBox.height
  // Handles anchored to MEDIA BODY center (header + body), never whole-card center.
  const handleTop = (HEADER_HEIGHT + mediaAreaHeight) / 2

  useGenerateVideoSlotDropHighlight(nodeShellRef)

  const showSectionHandles = showInspector && !referenceAssetsCollapsed
  const showRefAssetsProxyHandles = showInspector && referenceAssetsCollapsed
  const structuredAssetCategories: VideoStructuredAssetCategory[] = ['character', 'object', 'scene', 'reference']
  // VistoeraTASK-00153 §七/§八/§十/§十一 — the legacy "参考素材"
  // (`GENERIC_INPUT_HANDLE`) tray never got the same collapsed-proxy →
  // expanded-section-handle treatment every OTHER structured input
  // (character/product/scene/keyframe/reference-video/audio) already
  // has above; its ONLY DOM anchor was the always-mounted, fixed-
  // `handleTop` Handle, so a connected reference edge stayed pinned to
  // the generic node-center Y even once the node expanded and its OWN
  // "参考素材" section rendered lower down — exactly the reported anchor
  // drift. Fixed via the SAME single-shared-id collapsed-proxy ⇄
  // expanded-section-owned toggle every sibling section here already
  // uses (never per-edge — see the Handle block near this node's root
  // for why `GenerateImageNode.tsx`'s per-edge `imageReferenceAssetHandleId`
  // precedent does NOT apply to `generate_video`'s edges). Zero change
  // to the persisted `targetHandle`/binding either way.
  const showGenericRefHandles = showInspector
  // Stable (edge-id-keyed) dependency so `updateNodeInternals` only
  // re-fires when the SET of connected legacy-tray edges actually
  // changes, not on every unrelated render (spec §十七).
  const inputMaterialEdgeIdsKey = useMemo(() => inputMaterials.map((m) => m.edgeId).join('|'), [inputMaterials])

  useLayoutEffect(() => {
    updateNodeInternals(id)
  }, [
    id,
    nodeWidth,
    showInspector,
    referenceAssetsCollapsed,
    selectedKeyframeId,
    keyframeTimeline.length,
    showEnhancedPromptSection,
    inputMaterials.length,
    inputMaterialEdgeIdsKey,
    showGenericRefHandles,
    mergedGlobalInputAssetRefs.length,
    updateNodeInternals,
  ])

  const showFailedTaskError =
    task?.status === 'failed' &&
    Boolean(task.error_message) &&
    (!failedSubmitInputFingerprint || failedSubmitInputFingerprint === inputFingerprint)
  const collapsedError = !showInspector && (submitError || showFailedTaskError)
  const collapsedMediaResult = !showInspector && showActivePreview

  return (
    <div
      ref={nodeShellRef}
      className={`pcv-node pcv-node--media pcv-node--video-generator${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showInspector)} ${generationSlotIngressShellClass(showInspector)}${collapsedMediaResult ? ' is-collapsed-media-result' : ''}`}
      style={{ width: nodeWidth }}
    >
      {!showInspector ? (
        <Handle
          type="target"
          position={Position.Left}
          id={GENERATE_VIDEO_NODE_AGGREGATE_HANDLE}
          className="pcv-handle pcv-handle--aggregate"
          style={{ top: handleTop }}
          data-testid="video-node-aggregate-in-handle"
        />
      ) : null}
      {/* VistoeraTASK-00153 §七/§八/§十/§十一 forensic finding —
       * `generate_video` edges bound to `GENERIC_INPUT_HANDLE`
       * ("参考素材") are NEVER per-edge-scoped the way
       * `GenerateImageNode.tsx`'s OWN Related Assets row scopes them
       * (`canvasEdgeHandleHydration.ts`'s `normalizeGenerateVideoTargetHandle`
       * explicitly treats `GENERIC_INPUT_HANDLE` as "protected" and
       * returns it BARE, unlike `normalizeGenerateImageTargetHandle`'s
       * `imageReferenceAssetHandleId(dto.id)` scoping) — every "参考素材"
       * edge shares this ONE bare id, exactly like every other
       * structured category/section handle in this file (reference-
       * video/audio/character/product/scene are ALL single-shared-id
       * per SECTION, never per-edge). So this handle follows the SAME
       * established collapsed-proxy ⇄ expanded-section-owned toggle as
       * those siblings: fixed at `handleTop` whenever the "参考素材"
       * tray itself isn't the real interactive anchor (collapsed, OR
       * expanded with zero connected materials — nothing to anchor to
       * yet), and a REAL, section-owned `<Handle>` (same exact id)
       * inside the tray body below once it is. Exactly ONE DOM element
       * for this id exists at any time — never both — so React Flow
       * never sees a duplicate/ambiguous handle id for one node. */}
      {!showGenericRefHandles || inputMaterials.length === 0 ? (
        <Handle
          type="target"
          position={Position.Left}
          id={GENERIC_INPUT_HANDLE}
          className="pcv-handle pcv-handle--lg pcv-handle--reference pcv-handle--legacy-hidden"
          style={{ top: handleTop }}
        />
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        id="original-prompt-out"
        className="pcv-handle pcv-handle--prompt-source pcv-handle--legacy-hidden"
        style={{ top: handleTop }}
        data-testid="video-original-prompt-out-handle"
      />
      {!showInspector ? (
        <Handle
          type="target"
          position={Position.Left}
          id={VIDEO_ORIGINAL_PROMPT_IN_HANDLE}
          className="pcv-handle pcv-handle--prompt pcv-handle--legacy-hidden"
          style={{ top: handleTop }}
        />
      ) : null}
      {!showInspector ? (
        <Handle
          type="target"
          position={Position.Left}
          id={VIDEO_PROMPT_ENHANCEMENT_HANDLE}
          className="pcv-handle pcv-handle--prompt pcv-handle--legacy-hidden"
          style={{ top: handleTop }}
        />
      ) : null}
      {!showInspector ? (
        <>
          <Handle
            type="target"
            position={Position.Left}
            id={VIDEO_KEYFRAME_START_HANDLE}
            className="pcv-handle pcv-handle--keyframe-input pcv-handle--legacy-hidden"
            style={{ top: handleTop }}
          />
          <Handle
            type="target"
            position={Position.Left}
            id={VIDEO_KEYFRAME_MIDDLE_HANDLE}
            className="pcv-handle pcv-handle--keyframe-input pcv-handle--legacy-hidden"
            style={{ top: handleTop }}
          />
          <Handle
            type="target"
            position={Position.Left}
            id={VIDEO_KEYFRAME_END_HANDLE}
            className="pcv-handle pcv-handle--keyframe-input pcv-handle--legacy-hidden"
            style={{ top: handleTop }}
          />
        </>
      ) : null}
      {/* TASK-00058 forensic finding: these per-keyframe structured-asset
       * Handles used to mount ONLY while `showInspector` (expanded) —
       * but nodes always start COLLAPSED on fresh Canvas load, so a
       * persisted edge whose `target_handle` is one of these dynamic,
       * per-keyframe ids (e.g. `video-kf-middle-in::kf_XXXX` or
       * `reference-asset-in::kf_XXXX`) had no DOM `<Handle>` to resolve
       * until the node was manually expanded — `@xyflow/react` logged
       * "Couldn't create edge for target handle id" and retried on every
       * store/viewport update forever, the exact same class of bug fixed
       * for `ScriptGenerationNode`'s `script-source-in` handle. These are
       * already visually/interactively inert at all times via
       * `pcv-handle--legacy-hidden` (opacity 0, 2x2px, pointer-events
       * none) regardless of `showInspector`, so keeping them permanently
       * mounted — independent of expand state — has zero visual/
       * behavioral change while letting `@xyflow/react` always resolve a
       * real bounding rect for any legacy or current edge. */}
      {keyframeTimeline.length > 0
        ? keyframeTimeline
            .filter((entry) => entry.id !== selectedKeyframeId)
            .flatMap((entry) =>
              structuredAssetCategories.map((category) => (
                <Handle
                  key={`${category}::${entry.id}`}
                  type="target"
                  position={Position.Left}
                  id={videoStructuredAssetHandleId(category, entry.id)}
                  className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden"
                  style={{ top: handleTop }}
                />
              )),
            )
        : null}
      {/* VistoeraTASK-00143 P0-A forensic finding — the block above only
       * ever covers NON-selected keyframes' structured-asset handles
       * (the SELECTED keyframe's — or the bare global-scope, 0-keyframe
       * — character-in/product-in/scene-in/reference-asset-in, plus
       * `VIDEO_REFERENCE_VIDEO_HANDLE`/`VIDEO_AUDIO_REFERENCE_HANDLE`,
       * only ever get a real DOM `<Handle>` from the interactive section
       * components INSIDE the `showInspector`-gated inspector body
       * below). A whole-node COLLAPSE (`!showInspector`) unmounts that
       * entire inspector body — so any edge already bound to one of
       * those semantic ports had NO DOM anchor to resolve against while
       * collapsed (exactly the `@xyflow/react` "couldn't create edge for
       * target handle id" failure class already fixed above for the
       * OTHER handles at `handleTop`), and its visual endpoint would
       * fall back to whatever `@xyflow/react` guesses for a missing
       * handle — never the canonical "collapsed 生成结果区域左边缘垂直
       * 中点" spec §一/§二/§三 requires. Mounting one legacy-hidden,
       * non-interactive `<Handle>` per semantic port HERE — gated
       * `!showInspector` so it never collides with the real interactive
       * one the expanded inspector renders for the SAME id — guarantees
       * every currently-connected structured/reference-video/audio edge
       * resolves to this exact SAME `handleTop` Y coordinate as
       * `GENERATE_VIDEO_NODE_AGGREGATE_HANDLE` above: all of them
       * visually converge onto that one point the instant the node
       * collapses, with ZERO change to the persisted `targetHandle`/
       * structured binding itself (visual projection only, spec §二 hard
       * rule "视觉聚合绝对不能修改底层 binding / edge semantic"). */}
      {!showInspector ? (
        <>
          {structuredAssetCategories.map((category) => (
            <Handle
              key={`collapsed-aggregate::${category}`}
              type="target"
              position={Position.Left}
              id={videoStructuredAssetHandleId(category, keyframeTimeline.length > 0 ? selectedKeyframeId ?? undefined : undefined)}
              className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden"
              style={{ top: handleTop }}
            />
          ))}
          <Handle
            type="target"
            position={Position.Left}
            id={VIDEO_REFERENCE_VIDEO_HANDLE}
            className="pcv-handle pcv-handle--reference-video pcv-handle--legacy-hidden"
            style={{ top: handleTop }}
          />
          <Handle
            type="target"
            position={Position.Left}
            id={VIDEO_AUDIO_REFERENCE_HANDLE}
            className="pcv-handle pcv-handle--audio-reference pcv-handle--legacy-hidden"
            style={{ top: handleTop }}
          />
        </>
      ) : null}
      <div className="pcv-node-inner">
        <CanvasFunctionCardHeader
          variant="media"
          icon={def.icon}
          color={def.color}
          title={displayLabel}
          statusBadge={
            collapsedError ? (
              <span className="pcv-media-error-badge" title={submitError?.message || task?.error_message || t('media.failedFull')}>
                {t('media.failed')}
              </span>
            ) : null
          }
          actions={
            <>
              <Tooltip title={pinned ? t('board.unpinExpanded') : t('board.pinExpanded')} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className={`pcv-media-hover-btn pcv-pin-btn${pinned ? ' is-pinned' : ''}`}
                  aria-label={pinned ? t('board.unpinExpanded') : t('board.pinExpanded')}
                  aria-pressed={pinned}
                  data-testid="video-pin-toggle"
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePin()
                  }}
                >
                  {pinned ? <PushpinFilled /> : <PushpinOutlined />}
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.download} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.download} disabled={!showActivePreview} onClick={handleDownload}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={mediaHeader.delete} onClick={handleDeleteClick}>
                  <CloseOutlined />
                </button>
              </Tooltip>
            </>
          }
        />

        <div
          className={`pcv-media-body ${NODE_EXPAND_TRIGGER_CLASS}`}
          style={{ height: mediaAreaHeight }}
          onClick={
            showActivePreview
              ? (e) => {
                  e.stopPropagation()
                  onMediaSingleClick()
                }
              : () => {
                  nodeData.onExpandFunctionCardDetail?.(id)
                }
          }
          onDoubleClick={
            showActivePreview
              ? (e) => {
                  e.stopPropagation()
                  onMediaDoubleClick()
                }
              : undefined
          }
        >
          {showActivePreview && generatedVideoUrl ? (
            <>
              <CanvasVideoPreview
                ref={videoPreviewRef}
                key={generatedVideoUrl}
                assetId={generatedVideoUrl}
                src={generatedVideoUrl}
                poster={posterUrl}
                className="pcv-media-image"
                stickyPlayback={stickyPlayback}
                isNodeDragging={nodeData.isNodeDragging}
                playbackResetKey={nodeData.playbackResetKey}
              />
              {status === 'FAILED' ? (
                <div className="pcv-media-failure-overlay">
                  <span>{t('media.failedFull')}</span>
                </div>
              ) : null}
            </>
          ) : showGeneratingPlaceholder ? (
            <div className="pcv-media-placeholder pcv-media-placeholder--pending">
              <VideoCameraOutlined className="pcv-media-pending-icon" />
              <LoadingOutlined />
              <span className="pcv-media-placeholder-hint">{pendingPlaceholderText}</span>
            </div>
          ) : status === 'FAILED' ? (
            <div className="pcv-media-placeholder">
              <span className="pcv-media-placeholder-hint">{t('media.failedFull')}</span>
            </div>
          ) : (
            // Explicit Target Input model (spec §四) — the top media/
            // video-result area is ONLY ever "generated result / empty
            // placeholder" now, regardless of any in-progress connection
            // drag elsewhere on the Canvas; it must NEVER imply it is a
            // legal asset drop target again (root cause of images
            // silently landing in the legacy "参考素材" tray instead of
            // an explicit Character/Product/Scene/Other-Reference/
            // Keyframe port).
            <div className="pcv-media-placeholder">
              <VideoCameraOutlined />
              <span className="pcv-media-placeholder-hint">{t('media.videoEmpty')}</span>
            </div>
          )}

          {driftBannerText && !isBusy ? (
            <div className="pcv-gen-stale-banner nodrag" title={driftInspectorText || undefined}>
              {driftBannerText}
            </div>
          ) : null}
        </div>
      </div>

      <Handle type="source" position={Position.Right} id="video-out" className="pcv-handle pcv-handle--lg" style={{ top: handleTop }} />

      {showInspector ? (
        <div className="pcv-gen-inspector pcv-gen-input-body nodrag" data-testid="video-advanced-card-body">
          {/* ②③ Keyframe Timeline + Detail — true Tab / TabPanel visual
           * (P0_V2 §3-§9). No absolute connector / bridge measurement;
           * selected tab overlaps the panel top border via CSS only. */}
          <div
            ref={keyframeGroupRef}
            className={`pcv-video-keyframe-group${selectedKeyframeId ? ' has-selection' : ''}`}
            data-testid="video-keyframe-group"
            role="tablist"
          >
            {/* ② Keyframe Timeline — pure stage SELECTOR (spec §3-§6/§33) */}
            <KeyframeTimelineStrip
              workspaceId={workspaceId}
              timeline={keyframeTimeline}
              selectedKeyframeId={selectedKeyframeId}
              assignment={keyframeAssignment}
              disabled={controlsLocked}
              onSelectKeyframe={handleSelectKeyframe}
              onOpenKeyframeImageSource={handleOpenKeyframeImageSource}
              onAddKeyframe={handleAddKeyframe}
              onRemoveKeyframe={handleRemoveKeyframe}
              onRemoveKeyframeImage={handleRemoveKeyframeImage}
              onImportKeyframe={handleImportKeyframe}
              onClearKeyframes={handleClearKeyframesClick}
              sectionHandles={showInspector}
            />

            {/* ③ Selected Keyframe Detail — TabPanel for the selected tab */}
            {keyframeTimeline.length > 0 ? (
              <KeyframeDetailPanel
                workspaceId={workspaceId}
                entry={selectedKeyframeEntry}
                timeline={keyframeTimeline}
                dialogueSegments={dialogueSegments}
                dialogueAudioAssignment={dialogueAudioAssignment}
                disabled={controlsLocked}
                onUpdateEntry={(patch) => selectedKeyframeEntry && handleUpdateKeyframeEntry(selectedKeyframeEntry.id, patch)}
                onUpdateDurationToNext={(seconds) =>
                  selectedKeyframeEntry && handleUpdateKeyframeDurationToNext(selectedKeyframeEntry.id, seconds)
                }
                onCreateDialogueAudioNode={handleCreateDialogueAudioNode}
                onAddDialogueSegment={() => selectedKeyframeEntry && handleAddDialogueSegment(selectedKeyframeEntry.relativeTimeSec)}
                onSelectSourceNode={(nodeId) => {
                  const material = selectedKeyframeEntry && keyframeMaterialForEntry(keyframeAssignment, selectedKeyframeEntry)
                  if (material?.nodeType === 'generate_image') {
                    nodeData.onFocusVideoKeyframeSourceNode?.(nodeId)
                  } else {
                    nodeData.onSelectNode?.(nodeId)
                  }
                }}
                assetRefs={scopedInputAssetRefs}
                onRemoveAssetRef={handleRemoveInputAssetRef}
                onPickAssetImage={handlePickInputAssetImage}
                onAddAssetLabel={handleAddInputAssetLabel}
                onPreviewAssetRef={handlePreviewInputAssetRef}
                referenceAssetsCollapsed={referenceAssetsCollapsed}
                sectionHandles={showSectionHandles}
                mentionDraggable
              />
            ) : null}
          </div>

          <div ref={referenceInputSectionRef} data-testid="video-reference-input-anchor">
            {/* Connected edge materials (non-empty only) — empty-state removed
             * to avoid duplicating Input Assets "参考图片/视频" (P0_V2 §25). */}
            {inputMaterials.length > 0 ? (
              <div
                className="pcv-brief-section pcv-video-input-section"
                data-testid="video-reference-tray"
                data-video-input-port={GENERIC_INPUT_HANDLE}
                ref={referenceTrayRef}
              >
                <span className="pcv-brief-section-label">
                  {t('generation.videoReferenceLabel')}
                  <span className="pcv-brief-section-optional">{t('generation.videoReferenceHint')}</span>
                </span>
                {/* VistoeraTASK-00153 §七/§八/§十 — this tray only ever
                 * mounts while `showInspector` AND `inputMaterials.
                 * length > 0` (this whole block is nested inside the
                 * `{showInspector ? ... : null}` inspector body above,
                 * plus its own local `inputMaterials.length > 0`
                 * gate), so `showGenericRefHandles` is always true and
                 * this section is the CORRECT, real DOM anchor for
                 * `GENERIC_INPUT_HANDLE` right now (the collapsed/
                 * empty fallback above is NOT mounted whenever this
                 * is). Real section-owned `<Handle>`, not a child of
                 * `InputMaterialsRow` — every "参考素材" edge shares
                 * this ONE id regardless of which chip it is (spec
                 * finding above), so one handle for the whole tray is
                 * correct, never one per chip. */}
                <VideoInputSectionHandle
                  id={GENERIC_INPUT_HANDLE}
                  className="pcv-handle pcv-handle--lg pcv-handle--reference"
                  testId="video-reference-tray-in-handle"
                />
                <InputMaterialsRow
                  workspaceId={workspaceId}
                  materials={inputMaterials}
                  onDisconnect={(edgeId) => nodeData.onDisconnectMaterial?.(id, edgeId)}
                  onSelectSource={(nodeId) => nodeData.onSelectNode?.(nodeId)}
                  onEditText={(nodeId) => nodeData.onRequestEditText?.(nodeId)}
                  draggableMentions
                  hideLabel
                />
              </div>
            ) : null}
            {/* §九/§十 — the ONE combined "参考资产 N 项" collapsible section.
             * Collapsing/expanding is a PURE visual toggle (persisted via
             * the SAME lightweight `fields` pattern `pinned` above already
             * uses — a SEPARATE flag, never merged with it, spec §九/§二十)
             * — Keyframes above and the Prompt/Motion/Camera sections below
             * are NEVER inside this collapsible region. */}
            <div
              className={`pcv-video-reference-assets-toggle-wrap pcv-video-input-section${
                showRefAssetsProxyHandles ? ' has-proxy-handles' : ''
              }`}
            >
              {showRefAssetsProxyHandles ? (
                <div className="pcv-video-ref-assets-collapsed-handle-stack">
                  <VideoInputSectionHandle
                    id={GENERATE_VIDEO_REFERENCE_ASSETS_AGGREGATE_HANDLE}
                    className="pcv-handle pcv-handle--reference-assets-aggregate"
                    testId="video-reference-assets-aggregate-in-handle"
                  />
                  {structuredAssetCategories.map((category) => (
                    <VideoInputSectionHandle
                      key={`proxy-${category}`}
                      id={videoStructuredAssetHandleId(category)}
                      className="pcv-handle pcv-handle--structured-asset"
                    />
                  ))}
                  <VideoInputSectionHandle
                    id={VIDEO_REFERENCE_VIDEO_HANDLE}
                    className="pcv-handle pcv-handle--reference-video"
                    testId="video-reference-video-in-handle"
                  />
                  <VideoInputSectionHandle
                    id={VIDEO_AUDIO_REFERENCE_HANDLE}
                    className="pcv-handle pcv-handle--audio-reference"
                    testId="video-audio-reference-in-handle"
                  />
                </div>
              ) : null}
              <button
                ref={referenceAssetsToggleRef}
                type="button"
                className="pcv-video-reference-assets-toggle nodrag"
                data-testid="video-reference-assets-toggle"
                aria-expanded={!referenceAssetsCollapsed}
                onClick={toggleReferenceAssetsCollapsed}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleReferenceAssetsCollapsed()
                  }
                }}
              >
                {referenceAssetsCollapsed ? <RightOutlined /> : <DownOutlined />}
                <span className="pcv-video-reference-assets-toggle-label">{t('generation.shotReferenceAssetsSection')}</span>
                <span className="pcv-video-reference-assets-toggle-count" data-testid="video-reference-assets-count">
                  {t('generation.inputAssetsItemCount', { count: referenceAssetsTotalCount })}
                </span>
              </button>
            </div>

            {!referenceAssetsCollapsed ? (
              <div className="pcv-video-reference-assets-body" data-testid="video-reference-assets-body">
                {/* KEYFRAME_SCOPED_ASSETS_P0_V1 (P3/P10) — Characters/Products/
                 * Scenes/Other now live INSIDE the selected Keyframe's own
                 * Detail Panel above, scoped to that keyframe only. This
                 * global tray only remains for the LEGACY 0-keyframe / pure
                 * text-to-video state (spec §16) where there is no keyframe
                 * to scope assets to — never a second, competing list once
                 * keyframes exist. */}
                {keyframeTimeline.length === 0 ? (
                  <VideoInputAssetsSection
                    workspaceId={workspaceId}
                    refs={mergedGlobalInputAssetRefs}
                    disabled={controlsLocked}
                    variant="inline"
                    // §九/§十 — this instance only ever MOUNTS when the outer
                    // combined "参考资产" toggle above is already expanded
                    // (the parent conditional `{!referenceAssetsCollapsed ?
                    // ... : null}` handles hiding it entirely); passing
                    // `collapsed={false}` here ONLY suppresses this
                    // component's OWN internal duplicate header (never a
                    // SECOND "参考资产 N 项" label competing with the ONE
                    // combined header above), never a second collapse state.
                    collapsed={false}
                    sectionHandles={showSectionHandles}
                    onRemove={handleRemoveInputAssetRef}
                    onPickImage={handlePickInputAssetImage}
                    onAddLabel={handleAddInputAssetLabel}
                    onPreview={handlePreviewInputAssetRef}
                    mentionDraggable
                  />
                ) : null}

                {/* Reference Video (spec §P7 item 7/§P16/§P17) — a dedicated,
                 * GLOBAL structured input area, placed beside the other
                 * reference materials (never inside the Provider/Model
                 * footer). Rendered once regardless of keyframe count. */}
                <div
                  className="pcv-brief-section pcv-video-reference-video-section pcv-video-input-section"
                  data-testid="video-reference-video-section"
                  data-video-input-port={VIDEO_REFERENCE_VIDEO_HANDLE}
                >
                  {showSectionHandles ? (
                    <VideoInputSectionHandle
                      id={VIDEO_REFERENCE_VIDEO_HANDLE}
                      className="pcv-handle pcv-handle--reference-video"
                      testId="video-reference-video-in-handle"
                    />
                  ) : null}
                  <span className="pcv-brief-section-label">{t('generation.referenceVideoSection')}</span>
                  {referenceVideoSlot ? (
                    <div
                      className={`pcv-video-reference-video-card nodrag${referenceVideoSlot.ready ? '' : ' is-pending'}`}
                      data-testid="video-reference-video-card"
                      data-ready={referenceVideoSlot.ready ? 'true' : 'false'}
                    >
                      <VideoCameraOutlined className="pcv-video-reference-video-icon" />
                      <span className="pcv-video-reference-video-label">{referenceVideoSlot.sourceLabel}</span>
                      {!referenceVideoSlot.ready ? (
                        <span className="pcv-video-reference-video-pending-hint" data-testid="video-reference-video-pending-hint">
                          {t('generation.referenceVideoPendingHint')}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="pcv-video-reference-video-remove"
                        aria-label={t('generation.referenceVideoRemove')}
                        onClick={() => nodeData.onDisconnectMaterial?.(id, referenceVideoSlot.edgeId)}
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                  ) : (
                    <div className="pcv-video-reference-video-empty" data-testid="video-reference-video-empty">
                      {t('generation.referenceVideoEmptyHint')}
                    </div>
                  )}
                </div>

                {/* Audio (spec §五/§六/§七) — same dedicated, GLOBAL
                 * structured input area convention as Reference Video
                 * above, but MULTI-slot: `audioReferenceSlots` accumulates
                 * one entry per connected edge, never a single occupant. */}
                <div
                  className="pcv-brief-section pcv-video-audio-reference-section pcv-video-input-section"
                  data-testid="video-audio-reference-section"
                  data-video-input-port={VIDEO_AUDIO_REFERENCE_HANDLE}
                >
                  {showSectionHandles ? (
                    <VideoInputSectionHandle
                      id={VIDEO_AUDIO_REFERENCE_HANDLE}
                      className="pcv-handle pcv-handle--audio-reference"
                      testId="video-audio-reference-in-handle"
                    />
                  ) : null}
                  <span className="pcv-brief-section-label">{t('generation.audioReferenceSection')}</span>
                  {audioReferenceSlots.length > 0 ? (
                    <div className="pcv-video-audio-reference-list">
                      {audioReferenceSlots.map((slot) => {
                        const rawRole = audioAssetRoles[slot.edgeId]
                        const role: VideoAudioReferenceRole = isVideoAudioReferenceRole(rawRole) ? rawRole : VIDEO_AUDIO_ROLE_DEFAULT
                        return (
                          <div
                            key={slot.edgeId}
                            className={`pcv-video-audio-reference-card nodrag${slot.ready ? '' : ' is-pending'}`}
                            data-testid="video-audio-reference-card"
                            data-ready={slot.ready ? 'true' : 'false'}
                          >
                            <SoundOutlined className="pcv-video-audio-reference-icon" />
                            <span className="pcv-video-audio-reference-label">{slot.sourceLabel}</span>
                            {!slot.ready ? (
                              <span className="pcv-video-audio-reference-pending-hint" data-testid="video-audio-reference-pending-hint">
                                {t('generation.audioReferencePendingHint')}
                              </span>
                            ) : null}
                            <CanvasSelect
                              size="small"
                              className="pcv-video-audio-reference-role-select nodrag"
                              value={role}
                              aria-label={t('generation.audioReferenceRoleLabel')}
                              data-testid="video-audio-reference-role-select"
                              onChange={(v) => handleUpdateAudioAssetRole(slot.edgeId, v as VideoAudioReferenceRole)}
                              options={audioRoleSelectOptions}
                              popupMatchSelectWidth={false}
                            />
                            <button
                              type="button"
                              className="pcv-video-audio-reference-remove"
                              aria-label={t('generation.audioReferenceRemove')}
                              onClick={() => nodeData.onDisconnectMaterial?.(id, slot.edgeId)}
                            >
                              <CloseOutlined />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="pcv-video-audio-reference-empty" data-testid="video-audio-reference-empty">
                      {t('generation.audioReferenceEmptyHint')}
                    </div>
                  )}
                  {audioReferenceSlots.length > 0 && !audioInputSupportedBySelectedProvider ? (
                    <div className="pcv-gen-capability-warning nodrag" data-testid="video-audio-reference-unsupported-warning">
                      {t('generation.audioReferenceUnsupportedWarning')}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          {/* ⑤ Generation Parameters — BEFORE prompt (spec §26-§27/§33) */}
          <div className="pcv-gen-params-header" data-testid="video-parameters">
            <span className="pcv-brief-section-label">{t('generation.parametersSection')}</span>
            <button type="button" className="pcv-brief-collapse-toggle nodrag" onClick={() => setShowMoreParams((v) => !v)}>
              {showMoreParams ? <DownOutlined /> : <RightOutlined />} {t('generation.moreParams')}
            </button>
          </div>
          {formatMismatch ? (
            <div className="pcv-gen-capability-warning nodrag" data-testid="video-format-stale-banner">
              成片规格已改变，需要重新生成关键帧/视频。
            </div>
          ) : deliveryRenderStale ? (
            <div className="pcv-gen-capability-warning nodrag" data-testid="video-delivery-render-stale">
              交付清晰度已改变，可保留 AI 源素材并在合片时重新 conform。
            </div>
          ) : null}
          {/* P13 — the OLD quick-chip rows (16:9/9:16/1:1/4:3/3:4 +
           * 720p/1080p) duplicated these SAME two values as the compact
           * Selects below; removed entirely (never edit the same field
           * from two places, spec P13/P29). `isStoryboardBound`'s lock
           * state is now a plain inline hint, not a second control
           * surface. Provider/Model CAPABILITY drives `ratioOptions`/
           * `durationOptions`/`qualityOptions` (P14-P19, `channelRatio
           * Choices`/`channelDurationChoices`/`channelQualityChoices`
           * from `selectedProvider` — never Canvas-hardcoded). */}
          {isStoryboardBound ? (
            <div className="pcv-video-format-lock-hint" data-testid="video-production-format-lock-hint">
              🔒 {formatSourceLabel}
              {productionTargetWidth && productionTargetHeight ? ` · 目标 ${productionTargetWidth}×${productionTargetHeight}` : ''}
            </div>
          ) : null}
          {parameterAdjustmentHint ? (
            <div className="pcv-gen-field-hint" data-testid="video-parameter-adjustment-hint">
              {parameterAdjustmentHint}
            </div>
          ) : null}
          <div className="pcv-gen-controls nodrag" data-testid="video-production-format">
            {!aspectRatioLocked ? (
              <CanvasSelect
                size="small"
                className={
                  submitError?.field === 'aspect_ratio'
                    ? 'pcv-gen-ratio-select pcv-gen-field-error'
                    : 'pcv-gen-ratio-select'
                }
                value={ratio}
                disabled={formatControlsLocked}
                onChange={(v) => {
                  const aspect = normalizeProductionAspectRatio(v)
                  if (aspect) applyStandaloneFormat({ aspect_ratio: aspect })
                  else patchFields({ ratio: v })
                }}
                options={ratioOptions}
                popupMatchSelectWidth={false}
              />
            ) : (
              <span className="pcv-storyboard-production-format-summary">{lockedFormatSummary?.aspect_ratio || ratio}</span>
            )}
            <CanvasSelect
              size="small"
              className={
                submitError?.field === 'duration_seconds'
                  ? 'pcv-gen-duration-select pcv-gen-field-error'
                  : 'pcv-gen-duration-select'
              }
              value={durationSeconds}
              disabled={controlsLocked}
              onChange={(v) => patchFields({ durationSeconds: v })}
              options={durationOptions}
              popupMatchSelectWidth={false}
            />
            {!resolutionLocked ? (
              <CanvasSelect
                size="small"
                className={
                  submitError?.field === 'quality_tier'
                    ? 'pcv-gen-resolution-select pcv-gen-field-error'
                    : 'pcv-gen-resolution-select'
                }
                value={qualityTier}
                disabled={formatControlsLocked}
                onChange={(v) => {
                  const res = normalizeProductionResolution(v)
                  if (res && isStoryboardBound) {
                    patchFields({
                      qualityTier: res === '720p' ? '720P' : '1080P',
                    })
                    return
                  }
                  if (res) applyStandaloneFormat({ resolution: res })
                  else patchFields({ qualityTier: v })
                }}
                options={qualityOptions}
                popupMatchSelectWidth={false}
              />
            ) : (
              <span className="pcv-storyboard-production-format-summary">{lockedFormatSummary?.resolution || qualityTier}</span>
            )}
          </div>

          {showMoreParams && selectedProvider?.billing_tooltip ? (
            <div className="pcv-gen-controls pcv-gen-controls--more nodrag">
              <span className="pcv-gen-billing-hint" title={selectedProvider.billing_tooltip}>
                {selectedProvider.billing_hint}
              </span>
            </div>
          ) : null}

          {/* ⑥ Original + Enhanced Prompt */}
          <div
            className="pcv-brief-section pcv-video-input-section"
            data-testid="video-overall-prompt-section"
            data-video-input-port={VIDEO_ORIGINAL_PROMPT_IN_HANDLE}
            ref={originalPromptSectionRef}
          >
            {showInspector ? (
              <VideoInputSectionHandle
                id={VIDEO_ORIGINAL_PROMPT_IN_HANDLE}
                className="pcv-handle pcv-handle--prompt"
                testId="video-original-prompt-in-handle"
              />
            ) : null}
            <span className="pcv-brief-section-label">
              {t('generation.originalPromptSection')}
              {effectivePromptSource === 'ORIGINAL' ? (
                <span className="pcv-gen-result-status-badge is-ready" style={{ marginLeft: 8 }}>
                  {t('generation.effectivePromptBadge')}
                </span>
              ) : null}
            </span>
            <div ref={originalPromptEditorRef} data-testid="video-original-prompt-editor-anchor">
              <AssetMentionPromptEditor
                value={promptText}
                onChange={(next) => patchFields({ promptText: next })}
                aliasByEdgeId={combinedAliasByRefId}
                thumbUrlByEdgeId={combinedThumbByRefId}
                placeholder={t('generation.promptPlaceholderVideo')}
              />
            </div>
            <button
              type="button"
              className="pcv-brief-asset-add-btn nodrag"
              data-testid="video-prompt-enhance-btn"
              style={{ marginTop: 8 }}
              disabled={isBusy}
              onClick={(e) => {
                e.stopPropagation()
                nodeData.onCreateVideoPromptEnhancerNode?.(id)
              }}
            >
              {t('generation.promptEnhanceButton')}
            </button>
          </div>

          {showEnhancedPromptSection ? (
            <div
              className="pcv-brief-section pcv-video-input-section"
              data-testid="video-enhanced-prompt-section"
              ref={enhancedPromptSectionRef}
            >
              <VideoInputSectionHandle
                id={VIDEO_PROMPT_ENHANCEMENT_HANDLE}
                className="pcv-handle pcv-handle--prompt"
                testId="video-enhanced-prompt-in-handle"
              />
              <span className="pcv-brief-section-label">
                {t('generation.enhancedPromptSection')}
                {effectivePromptSource === 'ENHANCED' ? (
                  <span className="pcv-gen-result-status-badge is-ready" style={{ marginLeft: 8 }}>
                    {t('generation.effectivePromptBadge')}
                  </span>
                ) : null}
              </span>
              <Input.TextArea
                className="nodrag nowheel"
                value={enhancedPromptText}
                rows={4}
                placeholder={t('generation.enhancedPromptWaiting')}
                onChange={(e) =>
                  patchFields({
                    enhancedPromptText: e.target.value,
                    effectivePromptSource: e.target.value.trim() ? 'ENHANCED' : 'ORIGINAL',
                    ...markEnhancedPromptManualOverride(),
                  })
                }
              />
              <button
                type="button"
                className="pcv-brief-asset-add-btn nodrag"
                data-testid="video-clear-enhanced-prompt-btn"
                style={{ marginTop: 8 }}
                disabled={isBusy || !enhancedPromptText.trim()}
                onClick={(e) => {
                  e.stopPropagation()
                  patchFields(clearEnhancedPromptPatch())
                }}
              >
                {t('generation.clearEnhancedPrompt')}
              </button>
            </div>
          ) : null}

          {submitError ? <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{submitError.message}</div> : null}
          {disconnectedMentionCount > 0 ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
              {t('generation.disconnectedMentionsCount', { count: disconnectedMentionCount })}
            </div>
          ) : null}
          {driftInspectorText && !isBusy ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--stale">{driftInspectorText}</div>
          ) : null}
          {showFailedTaskError ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
              {localizeVideoGenerationSubmitError(
                { message: task.error_message ?? '', code: undefined, name: 'SaasApiError' },
                t,
              )}
            </div>
          ) : null}
          {/* Task spec §P7/§P8 — historical/stale Provider selection that
              Admin has since disabled/removed. Never silently falls
              back to another Provider and never fakes availability;
              blocks Generate below until the tenant re-selects. */}
          {persistedProviderUnavailable ? (
            <div
              className="pcv-gen-capability-warning pcv-gen-capability-warning--error"
              data-testid="video-provider-unavailable-warning"
            >
              {t('generation.providerUnavailable')}
            </div>
          ) : null}
          {providerExecutionBlocked ? (
            <div
              className="pcv-gen-capability-warning pcv-gen-capability-warning--error"
              data-testid="video-provider-execution-blocked-warning"
            >
              {providerExecutionBlockMessage}
            </div>
          ) : null}
          {/* Provider Capability Adapter — never silently drops (spec §34-§36) */}
          {capabilityWarnings.length > 0 ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="video-capability-warning">
              {t('generation.capabilityWarning', {
                provider: selectedProvider?.label || engineOptions.find((o) => o.value === engine)?.label || engine,
                capabilities: capabilityWarnings.map((w) => w.label).join('、'),
              })}
            </div>
          ) : null}
          {nodeAuthDenied ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="video-node-auth-denied">
              当前账号无权使用该功能节点
            </div>
          ) : null}
          {!nodeAuthDenied && (providerAuthDenied || noAllowedVideoProviders) ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="video-provider-auth-denied">
              {noAllowedVideoProviders ? '当前账号暂无可用执行通道' : '当前账号无权使用所选执行通道'}
            </div>
          ) : null}

          {/* ⑦ Footer — Provider | Skill | Generate (spec §30/§33) */}
          <div className="pcv-gen-footer nodrag" data-testid="video-footer" onClick={(e) => e.stopPropagation()}>
            {selectableProviderChannels.length > 0 && explicitProvider ? (
              <Tooltip title={t('media.providerChannel')} mouseEnterDelay={0.4}>
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                  data-testid="video-provider-select"
                  // Task spec §P8 — an unresolvable persisted channel id
                  // renders as the antd Select placeholder (never the
                  // raw, meaningless stored id string) alongside the
                  // explicit unavailable banner above.
                  value={
                    selectableProviderChannels.some((c) => c.id === providerChannel)
                      ? providerChannel
                      : undefined
                  }
                  placeholder={
                    persistedProviderUnavailable || providerAuthDenied
                      ? t('generation.providerUnavailableShort')
                      : t('media.providerChannel')
                  }
                  status={persistedProviderUnavailable || providerAuthDenied ? 'error' : undefined}
                  disabled={controlsLocked || nodeAuthDenied}
                  onChange={handleProviderChange}
                  options={selectableProviderChannels.map((channel) => ({
                    value: channel.id,
                    label: channel.label,
                    title: channel.billing_tooltip || channel.billing_hint,
                  }))}
                  popupMatchSelectWidth={false}
                />
              </Tooltip>
            ) : !explicitProvider ? (
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                data-testid="video-engine-select"
                value={engine}
                disabled={controlsLocked}
                onChange={(v) => patchFields({ engine: v })}
                options={engineOptions}
                popupMatchSelectWidth={false}
              />
            ) : null}
            {/* Task spec §P5/§P6 Case 4 — in-channel model/mode picker.
                Only rendered once a bound Provider channel actually
                exposes more than one allowed model (never for the three
                legacy single-model hardcoded channels). */}
            {modelChoices.length > 1 ? (
              <Tooltip title={t('media.providerModel')} mouseEnterDelay={0.4}>
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                  data-testid="video-provider-model-select"
                  value={selectedModelId}
                  disabled={controlsLocked}
                  onChange={(v) => patchFields({ modelId: v })}
                  options={modelChoices.map((m) => ({ value: m.id, label: m.label }))}
                  popupMatchSelectWidth={false}
                />
              </Tooltip>
            ) : null}
            <CanvasSelect
              size="small"
              className="pcv-gen-field-select pcv-gen-footer-select nodrag"
              data-testid="video-skill-select"
              value={fields.skillSelectionSource === 'EXPLICIT' ? VIDEO_SKILL_KEY : 'AUTO'}
              options={[
                { value: 'AUTO', label: t('generation.skillAuto') },
                { value: VIDEO_SKILL_KEY, label: t('generation.skillVideoGeneration') },
              ]}
              onChange={(v) =>
                patchFields(
                  v === 'AUTO'
                    ? { skillSelectionSource: 'AUTO', skillKey: null, skillVersion: null }
                    : { skillSelectionSource: 'EXPLICIT', skillKey: VIDEO_SKILL_KEY, skillVersion: VIDEO_SKILL_VERSION },
                )
              }
              popupMatchSelectWidth={false}
            />
            <button
              ref={runBtnRef}
              type="button"
              className="pcv-gen-footer-generate-btn"
              data-testid="video-generate-submit"
              // VISTOERA_LOCAL_LTX_MULTI_KEYFRAME_CAPABILITY_ENABLEMENT_AND_
              // PREVIEW_PRODUCTION_CLOSURE_P0_V2 §9 — block Generate whenever
              // the CURRENTLY selected/explicit engine's real capability_
              // matrix cannot serve the current keyframe/audio combination
              // (`capabilityWarnings`, never a hardcoded `engine === 'ltx'`
              // check — see `computeCapabilityWarnings`). Submitting anyway
              // would only round-trip into the server's own
              // `video_execution_planner` HTTP 400 (spec §9 "阻止"), so this
              // just avoids the dead click. LTX now advertises
              // MIDDLE_FRAME/END_FRAME_IMAGE = SUPPORTED, so this never
              // disables Generate for LTX regardless of keyframe count.
              disabled={
                isBusy ||
                !effectivePromptMeta.effectivePrompt.trim() ||
                capabilityWarnings.length > 0 ||
                persistedProviderUnavailable ||
                providerExecutionBlocked ||
                nodeAuthDenied ||
                providerAuthDenied ||
                noAllowedVideoProviders
              }
              onClick={handleGenerateClick}
            >
              {isBusy ? <LoadingOutlined /> : null} {runLabel}
            </button>
          </div>
        </div>
      ) : null}

      <CanvasActionPopover anchorEl={runBtnRef.current} open={regenerateChoiceOpen} onClose={() => setRegenerateChoiceOpen(false)} title={regenerate.title}>
        <p className="pcv-action-popover-desc">{t('generation.regenerateDescription')}</p>
        <div className="pcv-action-popover-actions">
          <button type="button" className="pcv-action-popover-btn" onClick={handleRegenerateSeparately}>
            {regenerate.separate}
          </button>
          <button type="button" className="pcv-action-popover-btn pcv-action-popover-btn--primary" onClick={handleOverwriteRegenerate}>
            {regenerate.overwrite}
          </button>
        </div>
      </CanvasActionPopover>

      <MediaLightbox
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false)
          setModalStartTime(0)
        }}
        kind="video"
        src={showActivePreview ? generatedVideoUrl || null : null}
        assetId={generatedVideoUrl || null}
        startTime={modalStartTime}
      />

      {/* Input Asset compact-card preview (spec §25 "点击进入已有的素材详情交互") */}
      <MediaLightbox
        open={!!assetPreview}
        onClose={() => setAssetPreview(null)}
        kind="image"
        src={assetPreview?.url || null}
        assetId={assetPreview?.url || null}
      />
    </div>
  )
}

export default memo(GenerateVideoNodeImpl)
